import {Injectable} from '@angular/core';
import {Observable, Subject, tap} from 'rxjs';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {ServiceAppModels} from '../models/serviceapp-models';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {HelperService} from './helper.service';
import {BenutzerModels} from '../models/benutzer-models';
import {LocalStorageService} from '../local-storage.service';
import {SwissSettingsService} from './swiss-settings.service';
import {DatumFormatPipe} from '../pipes/datum-format.pipe';
import {v4 as uuidv4} from 'uuid';
import {DokumentModels} from '../models/dokument-models';
import {GlobalModels} from '../models/global-models';
import {MessageBoxService} from './message-box.service';
import {MatDialog} from '@angular/material/dialog';
import {TerminDetailsComponent} from '../home-layout/service-app/termin-details/termin-details.component';
import moment from 'moment';
import {DataService} from '../time-app/services/data.service';
import {Employees} from '../time-app/models/employees-model';
import {ZahlenFormatPipe} from '../pipes/zahlen-format.pipe';
import {UserAuthenticationService} from './user-authentication.service';
import {SWISS_SERVICE_APP_DB_STORE_NAME} from '../models/local-storage-names';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Platform} from '@angular/cdk/platform';
import {ServiceAppDocumentTaskFactory, ServiceAppSyncTask} from "./service-app-document-synchronisation-service";
import SA_ServicePos = ServiceAppModels.SA_ServicePos;
import SwissAppMandant = SwissAppConfigModels.SwissAppMandant;
import SA_RapportFile = ServiceAppModels.SA_RapportFile;
import SA_BildFile = ServiceAppModels.SA_BildFile;
import SA_ChecklistFile = ServiceAppModels.SA_ChecklistFile;
import SA_Document = ServiceAppModels.SA_Document;
import SA_ChecklistUploadElement = ServiceAppModels.SA_ChecklistUploadElement;
import SwissAppConfig = SwissAppConfigModels.SwissAppConfig;
import Anwender = BenutzerModels.Anwender;
import DEFAULT_SYNC_PERIOD = ServiceAppModels.DEFAULT_SYNC_PERIOD;

@Injectable({
    providedIn: 'root',
})
export class ServiceAppService {
    private dbStorageSA = 'ServiceAppDB';
    private dbStorageSA_Doku = 'ServiceAppDB_Doku';
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private swissAppUserConfig?: SwissAppConfigModels.SwissAppUserConfig;
    private anwender?: BenutzerModels.Anwender;

    private employee?: ServiceAppModels.SA_Employees;
    private anwenderReaded = false;
    private currTermin?: ServiceAppModels.SA_Objekt;
    private dbStammdaten?: ServiceAppModels.Stammdaten;
    private dbMitarbeiter?: ServiceAppModels.SA_Employees[];
    private dbMitarbeiterAnsaetze?: ServiceAppModels.SA_EmployeeRates[];
    private dbKostenstellen?: ServiceAppModels.SA_CostCenter[];
    private dbKostenstellenAnsaetze?: ServiceAppModels.SA_CostCenterRates[];
    private dbSpesen?: ServiceAppModels.SA_Expenses[];
    private sA_Objects?: ServiceAppModels.SA_Objekt[];
    private sA_DocumentAssignments?: ServiceAppModels.SA_DocumentAssignment[];
    private sA_ServicePos?: ServiceAppModels.SA_ServicePos[];
    private sA_Config?: ServiceAppModels.SA_Config;
    private dbAuftragAdressen?: ServiceAppModels.SA_ObjectAdress[];
    private dbAdressen?: ServiceAppModels.SA_Adress[];
    private dbAuftragsZeiten?: ServiceAppModels.SA_TimeEntry[];
    private dbTermine?: ServiceAppModels.SA_Objekt[] = [];
    private dbDokumente: ServiceAppModels.SA_Document[] = [];
    private dbDokumentZuordnungen?: ServiceAppModels.SA_DocumentAssignment[];
    private dayDateValue: Date = new Date();
    private dbAuftragsProdukte?: ServiceAppModels.SA_ProductEntry[];
    private dbProdukte: ServiceAppModels.Produkt[] = [];
    private dbRapporte: ServiceAppModels.SA_RapportFile[] = [];
    private dbBilder: ServiceAppModels.SA_BildFile[] = [];
    private dbChecklistenTemplates: ServiceAppModels.CheckListeTemplate[] = [];
    private dbChecklisten: ServiceAppModels.SA_ChecklistFile[] = [];
    private dbDokumenteToUpload: string[] = [];
    private checklistUploadElement?: ServiceAppModels.SA_ChecklistUploadElement;
    private dbSA_ServicePos: ServiceAppModels.SA_ServicePos[] = [];
    private dbSA_TimeControlEntries: ServiceAppModels.SA_TimeControlEntry[] = [];
    private dbSA_TimeControlDays: ServiceAppModels.SA_TimeControlDay[] = [];
    private dataForAppointment?: ServiceAppModels.SA_DataForAppointment;
    private doSaveLocalPics = false;
    private doSaveLocalCheckLists = false;
    private timeRecordingItems: ServiceAppModels.TimeRecordingItem[] = [];
    private lastSchedulerView = 'WorkWeek';
    private employees: Employees[] = [];
    private currTimeEmployee?: Employees;
    private timesChanged$: Subject<undefined> = new Subject<undefined>();

    /* Service-Positionen für Upload */

    private rapportLogoLoaded = false;
    private logoBlob?: Blob;
    private logoBase64Str = '';
    private doCancelSync = false;
    private reloadAppointmentsFunc?: () => void;
    private reloadOrdersFunc?: () => void;
    private reloadSASyncStateFunc?: (message: string) => void;
    private reloadSASyncStateProgressBarFunc?: (value: number) => void;
    private reloadSASyncStateDocuFunc?: (anzahl: number, total: number) => void;
    private reloadSAHeaderObjectFunc?: () => void;

    constructor(private http: HttpClient,
                private userAuthService: UserAuthenticationService,
                private helperService: HelperService,
                private _datumFormatPipe: DatumFormatPipe,
                private _zahlenFormatPipe: ZahlenFormatPipe,
                private svcSwissSettings: SwissSettingsService,
                public dialog: MatDialog,
                private svcMessageBox: MessageBoxService,
                private localStorageService: LocalStorageService,
                private serviceAppDocumentTaskFactory: ServiceAppDocumentTaskFactory,
                private dataService: DataService,// Für Berechtigungen aus der ZeiterfassungsApp => Tag wieder öffnen
                private platform: Platform
    ) {
        this.helperService.getSwissWebServiceConfig$().pipe(
            takeUntilDestroyed(),
            tap((config) => {
                this.webServiceConfig = config;
            })
        ).subscribe();

        if (this.svcSwissSettings.getWebConfig().debugMode) {
            console.log('ServiceApp-Service - Constructor');
        }
        // Sobald die Settings geladen sind, wird die Methode loadSwissAppConfigs aufgerufen
        this.svcSwissSettings.getSwissAppUserConfig$().pipe(
            takeUntilDestroyed(),
            tap(async (userConf) => {
                this.swissAppUserConfig = userConf;
                await this.loadDataForServiceApp();
            })
        ).subscribe();
    }

    public async loadDataForServiceApp(): Promise<boolean> {
        this.anwenderReaded = false;
        await this.getAnwenderAsync();
        await this.loadAdressenFromIndexedDB();
        await this.loadObjektAdressenFromIndexedDB();
        await this.loadProductEntriesFromIndexedDB();
        await this.loadSettingsFromIndexedDBWithFallBack();
        await this.loadChecklistFromIndexedDB();
        await this.loadTimeControlDaysFromIndexedDB();
        if (await this.loadMasterDataFromIndexedDB()) {
            if (await this.loadObjekteFromIndexedDB(false)) {
                return await this.loadTimeEntriesFromIndexedDB();
            }
        }
        return false;
    }


    registerAppointmentsFunc(fn: () => void): void {
        this.reloadAppointmentsFunc = fn;
    }

    registerOrdersFunc(fn: () => void): void {
        this.reloadOrdersFunc = fn;
    }

    /* Status der Synchronisation mitteilen */
    registerSASyncStateFunc(fn: (message: string) => void): void {
        this.reloadSASyncStateFunc = fn;
    }

    registerSASyncStateProgressBarFunc(fn: (value: number) => void): void {
        this.reloadSASyncStateProgressBarFunc = fn;
    }

    registerSASyncStateDocuFunc(fn: (anzahl: number, total: number) => void): void {
        this.reloadSASyncStateDocuFunc = fn;
    }

    registerSAHeaderObjectFunc(fn: () => void): void {
        this.reloadSAHeaderObjectFunc = fn;
    }

    getAnwenderAsync = async (): Promise<BenutzerModels.Anwender> => {
        return new Promise((resolve) => {
            if (this.anwenderReaded) {

                // TODO Redundanter Coder
                this.dataService.getAllEmployee().then(res => {
                    this.employees = res;
                    this.currTimeEmployee = this.employees.filter(x => x.employeeID === this.swissAppUserConfig?.employeeID)[0];
                });

                return resolve(this.userAuthService.getCurrentAuthenticatedUser()!);
            } else {
                this.loadAnwenderFromIndexedDB().then(() => {
                    this.anwenderReaded = true;
                    if (this.svcSwissSettings.getWebConfig().debugMode) {
                        console.log('Anwender geladen', this.anwender);
                    }
                    this.dataService.getAllEmployee().then(res => {
                        this.employees = res;
                        this.currTimeEmployee = this.employees.filter(x => x.employeeID === this.swissAppUserConfig?.employeeID)[0];
                    });
                    return resolve(this.userAuthService.getCurrentAuthenticatedUser()!);
                });
            }
        });
    }

    getDBAuftragsProdukte(): ServiceAppModels.SA_ProductEntry[] {
        if (this.dbAuftragsProdukte) {
            return this.dbAuftragsProdukte.filter((x) => !x.deleted && x.serviceID === this.currTermin?.serviceID);
        } else {
            return [];
        }
    }

    getAnwender(): Anwender | undefined {
        return this.userAuthService.getCurrentAuthenticatedUser();
    }

    getTimeEmployee(): Promise<Employees | undefined> {
        return new Promise((resolve) => {
            if (!this.swissAppUserConfig) {
                this.swissAppUserConfig = this.svcSwissSettings.getSwissAppUserConfig();
            }

            if (this.currTimeEmployee) {
                resolve(this.currTimeEmployee);
            } else {
                this.dataService.getAllEmployee().then(res => {
                    if (res) {
                        this.employees = res;
                        this.currTimeEmployee = this.employees.filter(x => x.employeeID === this.swissAppUserConfig?.employeeID)[0];
                        resolve(this.currTimeEmployee);
                    } else {
                        resolve(undefined);
                    }
                });
            }
        });
    }

    getDBAuftragsZeiten(terminBezogen: boolean): ServiceAppModels.SA_TimeEntry[] {
        if (this.dbAuftragsZeiten) {
            if (terminBezogen && this.currTermin) {
                return this.dbAuftragsZeiten.filter((az) => az.serviceID === this.currTermin?.serviceID);
            } else if (terminBezogen) {
                return [];
            }// Terminbezogen ohne selektierten Auftrag
            else {
                return this.dbAuftragsZeiten;
            } // Zeiten aller Aufträge zurückgeben
        } else {
            return [];
        }
    }

    getDBSpesen(): ServiceAppModels.SA_Expenses[] {
        if (this.dbSpesen) {
            return this.dbSpesen;
        } else {
            return [];
        }
    }

    getDBMitarbeiter(): ServiceAppModels.SA_Employees[] {
        if (this.dbMitarbeiter) {
            return this.dbMitarbeiter;
        } else {
            return [];
        }
    }

    getAktuellerMitarbeiter(): ServiceAppModels.SA_Employees | undefined {
        if (this.employee) {
            return this.employee;
        }
        return undefined;
    }

    getDBMitarbeiterAnsaetze(): ServiceAppModels.SA_EmployeeRates[] {
        if (this.dbMitarbeiterAnsaetze) {
            return this.dbMitarbeiterAnsaetze;
        } else {
            return [];
        }
    }

    /* Diese Funktion ermittelt den Mitarbeiter-Ansatz per Stichdatum der Position */
    getDBMitarbeiterAnsatz(datum: Date | string, employeeID: number): number {
        if (this.svcSwissSettings.getWebConfig().debugMode) {
            console.log('getDBMitarbeiterAnsatz() - Start', datum, employeeID);
        }
        let dateValue: Date;

        if (typeof datum === 'string') {
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('getDBMitarbeiterAnsatz() - Datum ist ein string');
            }
            if (String(datum).indexOf('T') > -1) {
                dateValue = this._datumFormatPipe.convertStringENToDate(datum);
            } else {
                dateValue = this._datumFormatPipe.convertStringToDate(datum);
            }
        } else {
            const strDatum: String = String(datum);
            const dateString = strDatum.substring(6, 10) + '-' + strDatum.substring(3, 5) + '-' + strDatum.substring(0, 2) + 'T00:00:00.000';
            dateValue = new Date(dateString);
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log(datum, strDatum, dateString, dateValue);
            }
        }

        let resAnsatz = 0.0;
        if (this.dbMitarbeiterAnsaetze) {
            const currMitarbeiterAnsaetze: ServiceAppModels.SA_EmployeeRates[] = this.dbMitarbeiterAnsaetze.filter((m) => m.fK_EmployeeID == employeeID);
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('getDBMitarbeiterAnsatz()', currMitarbeiterAnsaetze, dateValue);
            }
            for (const ansatz of currMitarbeiterAnsaetze) {
                if (this.svcSwissSettings.getWebConfig().debugMode) {
                    console.log('getDBMitarbeiterAnsatz():Ansatz prüfen', ansatz.validFrom, ansatz.validTo, dateValue);
                }
                const currValidFrom = new Date(ansatz.validFrom);
                const currValidTo = new Date(ansatz.validTo);
                if (currValidFrom <= dateValue && dateValue <= currValidTo) {
                    resAnsatz = ansatz.overheadRate1;
                    break;
                }
            }
        }
        return resAnsatz;
    }

    getDBKostenstellenRegieAnsatz(datum: Date | string, costCenterID: number): number {
        let dateValue: Date;
        let resAnsatz = 0.0;

        if (typeof datum === 'string') {
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('getDBKostenstellenRegieAnsatz() - Datum ist ein string');
            }
            if (String(datum).indexOf('T') > -1) {
                dateValue = this._datumFormatPipe.convertStringENToDate(datum);
            } else {
                dateValue = this._datumFormatPipe.convertStringToDate(datum);
            }
        } else {
            const strDatum: String = String(datum);
            const dateString = strDatum.substring(6, 10) + '-' + strDatum.substring(3, 5) + '-' + strDatum.substring(0, 2) + 'T00:00:00.000';
            dateValue = new Date(dateString);
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log(datum, strDatum, dateString, dateValue);
            }
        }

        if (this.dbKostenstellenAnsaetze) {
            const costCenterRates: ServiceAppModels.SA_CostCenterRates[] = this.dbKostenstellenAnsaetze.filter(x => x.fK_CostCenterID === costCenterID);
            for (const rate of costCenterRates) {
                const currValidFrom = new Date(rate.validFrom);
                const currValidTo = new Date(rate.validTo);
                if (currValidFrom <= dateValue && dateValue <= currValidTo) {
                    resAnsatz = rate.overheadRate1;
                    break;
                }
            }
        }
        return resAnsatz;
    }

    getAdressen(): ServiceAppModels.SA_Adress[] {
        if (this.dbAdressen) {
            return this.dbAdressen;
        } else {
            return [];
        }
    }

    getDBTermine(): ServiceAppModels.SA_Objekt[] {
        if (this.dbTermine) {
            return this.dbTermine;
        } else {
            return [];
        }
    }

    addAuftragByRowID(rowID: string): Promise<boolean> {
        return new Promise((resolve) => {
            const url = this.webServiceConfig!.saUrl;
            this.http.get<ServiceAppModels.SA_Objekt>(url + '/ObjektAuftrag?rowID=' + rowID + '&mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo))
                .subscribe({
                    next: async (res) => {
                        if (res) {
                            this.dbTermine!.push(res);
                            this.currTermin = res;
                            await this.saveDBTermineInIndexedDB();
                            resolve(true);
                        } else {
                            this.svcMessageBox.showError('Konnte Termin nicht anlegen', 'Fehler!!');
                            resolve(false);
                        }
                    }, error: () => {
                        this.svcMessageBox.showError('Konnte Termin nicht anlegen', 'Fehler!!');
                        resolve(false);
                    },
                });
        });
    }

    async delAuftragByRowID(rowID: string): Promise<boolean> {
        const index = this.dbTermine!.findIndex((x) => x.rowID === rowID);
        if (index > -1) {
            this.dbTermine!.splice(index, 1);
            await this.saveDBTermineInIndexedDB();
            if (this.reloadSAHeaderObjectFunc) {
                this.reloadSAHeaderObjectFunc();
            }
        }
        return true;
    }

    async saveDBTermineInIndexedDB(): Promise<void> {
        if (this.dbTermine) {
            this.sA_Objects = this.dbTermine;
            await this.localStorageService.add('objekte', this.sA_Objects, this.dbStorageSA);
            if (this.reloadAppointmentsFunc) {
                this.reloadAppointmentsFunc();
            }
            if (this.reloadOrdersFunc) {
                this.reloadOrdersFunc();
            }
        }
    }

    loadServicePosFromIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('servicePos', this.dbStorageSA)
                .then((value) => {
                    if (value === false) {
                        // Es konnte keinen Wert in der indexeddb für servicePos ermittelt werden
                        resolve(true);
                    } else {
                        this.dbSA_ServicePos = (value as ServiceAppModels.SA_ServicePos[]);
                        resolve(true);
                    }
                });
        });
    }

    loadTimeControlEntriesFromIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('timeControlEntries', this.dbStorageSA)
                .then((value) => {
                    if (value === false) {
                        // Es konnte keinen Wert in der indexeddb für timeControlEntries ermittelt werden
                        resolve(true);
                    } else {
                        this.dbSA_TimeControlEntries = (value as ServiceAppModels.SA_TimeControlEntry[]);
                        resolve(true);
                    }
                });
        });
    }

    getTimeEntryByServicePosID(servicePosID: string): Promise<ServiceAppModels.SA_TimeControlEntry | undefined> {
        return new Promise((resolve) => {
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('getTimeEntryByServicePosID', this.dbSA_TimeControlEntries, servicePosID);
            }
            this.loadTimeControlEntriesFromIndexedDB().then(res => {
                if (res) {
                    if (this.dbSA_TimeControlEntries && this.dbSA_TimeControlEntries.length > 0) {
                        resolve(this.dbSA_TimeControlEntries.filter(x => x.servicePosID === servicePosID)[0]);
                    }
                }
                resolve(undefined);
            });
        });
    }

    saveServicePosInIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.dbSA_ServicePos) {
                this.localStorageService.add('servicePos', this.dbSA_ServicePos, this.dbStorageSA);
                resolve(true);
            } else {
                resolve(true);
            }
        });
    }

    saveTimeControlEntriesInIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.dbSA_TimeControlEntries) {
                this.localStorageService.add('timeControlEntries', this.dbSA_TimeControlEntries, this.dbStorageSA);
                resolve(true);
            } else {
                resolve(true);
            }
        });
    }

    showAppointmentDetails(isFinished = false) {
        this.dataForAppointment = {
            sA_Termin: this.currTermin, sA_Anwender: this.userAuthService.getCurrentAuthenticatedUser(), isFinished
        };

        const dialogRef = this.dialog.open(TerminDetailsComponent, {
            disableClose: true, data: this.dataForAppointment, panelClass: 'swissdefault-dialog-container',
        });

        dialogRef.afterClosed().subscribe({
            next: (result) => {
                const appointmentDetails = result;
                if (appointmentDetails) {
                    // Update Informations
                    this.saveTerminDetails(appointmentDetails);
                    if (this.svcSwissSettings.getWebConfig().debugMode) {
                        console.log('Dialog Resultat (sa_AppointmentDetails): ', appointmentDetails);
                    }
                }
            },
        });
    }

    setDBTermine(termine: ServiceAppModels.SA_Objekt[]) {
        this.dbTermine = termine;
        this.saveDBTermineInIndexedDB();
    }

    getAuftragAdressen(): ServiceAppModels.SA_ObjectAdress[] {
        if (this.dbAuftragAdressen) {
            return this.dbAuftragAdressen;
        } else {
            return [];
        }
    }

    getDBProdukte(): ServiceAppModels.Produkt[] {
        if (this.dbProdukte) {
            return this.dbProdukte;
        } else {
            return [];
        }
    }

    getDBObjektDokumente(): ServiceAppModels.SA_Document[] {
        if (this.dbDokumente) {
            return this.dbDokumente;
        } else {
            return [];
        }
    }

    getDBTerminDokumente(): ServiceAppModels.SA_DocumentAssignment[] {
        if (this.dbDokumentZuordnungen) {
            return this.dbDokumentZuordnungen;
        } else {
            return [];
        }
    }

    getTerminDokumenteList(serviceID: string, objectID: string): ServiceAppModels.TerminDokumentListElement[] {
        const documentList: ServiceAppModels.TerminDokumentListElement[] = [];
        const base = this.getDBTerminDokumente().filter((d) => d.fK_ServiceID === serviceID);
        base.forEach((terminDocument) => {
            const document = this.getDBObjektDokumente().filter((d) => d.documentID === terminDocument.documentID)[0];
            if (!document) {
                return;
            }

            if (document.sA_TransDocType === 1 || document.sA_TransDocType === 2 || document.sA_TransDocType === 3) {
                const kundenAdresse = this.getAuftragsAdresse(objectID, GlobalModels.AdressTypes.Kunde);
                const kontaktAdresse = this.getAuftragsKontaktAdresse(objectID, GlobalModels.AdressTypes.Kunde);

                let dokuTyp: DokumentModels.DokumentTyp = DokumentModels.DokumentTyp.Dokument;
                if (document.sA_TransDocType === 1) {
                    dokuTyp = DokumentModels.DokumentTyp.Rapport; // Lieferschein
                } else if (document.sA_TransDocType === 2) {
                    dokuTyp = DokumentModels.DokumentTyp.Checkliste;
                }
                const imageFile: string = this.getImageFile(document.fileName);
                const dokuQuelle = DokumentModels.DokumentSource.SA_DBDokumente;

                const listElement: ServiceAppModels.TerminDokumentListElement = {
                    imageFile,
                    creator: document.createdBy,
                    id: document.documentID,
                    documentName: document.fileName,
                    description: document.description,
                    datum: document.createdDate || new Date(),
                    firstName: kundenAdresse?.firstName,
                    lastName: kundenAdresse?.lastName,
                    ansprechVorname: kontaktAdresse?.firstName,
                    ansprechName: kontaktAdresse?.lastName,
                    dokumentTyp: dokuTyp,
                    documentQuelle: dokuQuelle,
                    lastModified: document.modifiedDate
                };

                documentList.push(listElement);
            }
        });

        this.getChecklistenByServiceID(serviceID).forEach((terminIndexedDBCheckliste) => {
            const kundenAdresse = this.getAuftragsAdresse(objectID, GlobalModels.AdressTypes.Kunde);
            const kontaktAdresse = this.getAuftragsKontaktAdresse(objectID, GlobalModels.AdressTypes.Kunde);

            const listElement: ServiceAppModels.TerminDokumentListElement = {
                imageFile: 'pdf.png',
                creator: terminIndexedDBCheckliste.createdBy,
                id: terminIndexedDBCheckliste.documentID,
                documentName: terminIndexedDBCheckliste.fileName,
                description: terminIndexedDBCheckliste.description,
                datum: terminIndexedDBCheckliste.createdDate,
                firstName: kundenAdresse?.firstName,
                lastName: kundenAdresse?.lastName,
                ansprechVorname: kontaktAdresse?.firstName,
                ansprechName: kontaktAdresse?.lastName,
                dokumentTyp: DokumentModels.DokumentTyp.Checkliste,
                documentQuelle: DokumentModels.DokumentSource.SA_DBChecklisten,
            };
            documentList.push(listElement);
        });

        this.getRapporteByServiceID(serviceID).forEach((terminDokument) => {
            const kundenAdresse = this.getAuftragsAdresse(objectID, GlobalModels.AdressTypes.Kunde);
            const kontaktAdresse = this.getAuftragsKontaktAdresse(objectID, GlobalModels.AdressTypes.Kunde);

            const listElement: ServiceAppModels.TerminDokumentListElement = {
                imageFile: 'pdf.png',
                creator: terminDokument.createdBy,
                id: terminDokument.documentID,
                documentName: terminDokument.fileName,
                description: terminDokument.description,
                datum: terminDokument.createdDate,
                firstName: kundenAdresse?.firstName,
                lastName: kundenAdresse?.lastName,
                ansprechVorname: kontaktAdresse?.firstName,
                ansprechName: kontaktAdresse?.lastName,
                dokumentTyp: DokumentModels.DokumentTyp.Rapport,
                documentQuelle: DokumentModels.DokumentSource.SA_DBRapporte,
            };
            documentList.push(listElement);
        });

        return documentList;
    }

    getImageFile(fileName: string): string {
        let ext = '';
        let imageFile = 'unknown.png';
        if (fileName) {
            ext = fileName.substring(fileName.lastIndexOf('.') + 1);
        }

        if (ext) {
            switch (ext) {
                case 'bmp':
                    imageFile = 'photo.png';
                    break;
                case 'doc':
                    imageFile = 'doc.jpg';
                    break;
                case 'docx':
                    imageFile = 'doc.jpg';
                    break;
                case 'gif':
                    imageFile = 'photo.png';
                    break;
                case 'jpg':
                    imageFile = 'photo.png';
                    break;
                case 'jpeg':
                    imageFile = 'photo.png';
                    break;
                case 'msg':
                    imageFile = 'email.png';
                    break;
                case 'pdf':
                    imageFile = 'pdf.png';
                    break;
                case 'png':
                    imageFile = 'photo.png';
                    break;
                case 'tiff':
                    imageFile = 'text.png';
                    break;
                case 'txt':
                    imageFile = 'text.png';
                    break;
                case 'xlm':
                    imageFile = 'xml.png';
                    break;
                case 'xls':
                    imageFile = 'xls.png';
                    break;
                case 'xlsx':
                    imageFile = 'xls.png';
                    break;
                case 'zip':
                    imageFile = 'zip.png';
                    break;
                default:
                    imageFile = 'unknown.png';
            }
        }
        return imageFile;
    }


    async updateDBDokumentDescription(documentID: string, description: string, dataSource: DokumentModels.DokumentSource) {
        if (dataSource === DokumentModels.DokumentSource.FromWebService) {
            if (this.dbDokumente) {
                const index = this.dbDokumente.findIndex((x) => x.documentID == documentID);
                const terminDocument = this.dbDokumente[index];
                terminDocument.description = description;
                terminDocument.uploadRequired = true;
                terminDocument.modifiedBy = this.userAuthService.getCurrentAuthenticatedUser()!.firstName;
                terminDocument.modifiedDate = new Date();
                this.dbDokumente[index] = terminDocument;
                await this.localStorageService.add('objektDokumente', this.dbDokumente, this.dbStorageSA);
            }
        } else if (dataSource === DokumentModels.DokumentSource.SA_DBBilder) {
            if (this.dbBilder) {
                const index = this.dbBilder.findIndex((x) => x.documentID == documentID);
                const terminDocument = this.dbBilder[index];
                terminDocument.description = description;
                terminDocument.uploaded = false;
                this.dbBilder[index] = terminDocument;
                await this.localStorageService.add('bilder', this.dbBilder, this.dbStorageSA);
            }
        }
    }

    downloadDokument(document: ServiceAppModels.SA_Document): Observable<any> {
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const url = this.webServiceConfig!.dokuUrl;
        return this.http.get<any>(url + '/DownloadDatei?id=' + document.documentID + '&loginname=' + this.userAuthService.getCurrentAuthenticatedUser()?.loginname, {headers, responseType: 'blob' as 'json'});
    }

    async addDokumentInDB(dokumentFile: ServiceAppModels.DokumentDBFile) {
        await this.localStorageService.add(dokumentFile.documentID, dokumentFile, this.dbStorageSA_Doku);
    }

    // Diese Funktion ersetzt das PDF-Dokument in der Indexed-DB,
    // Wenn eine Mutation im PDF-WebViewer gespeichert wurde
    async replaceDokumentInDB(documentID: string, binary: Blob, uploaded: boolean) {
        await this.localStorageService
            .get(documentID, this.dbStorageSA_Doku)
            .then((doc) => {
                if (doc) {
                    const dokumentFile = doc as ServiceAppModels.DokumentDBFile;
                    dokumentFile.binary = binary;
                    dokumentFile.uploaded = uploaded;
                    this.localStorageService.add(documentID, dokumentFile, this.dbStorageSA_Doku);
                }
            });
    }

    async loadAdressenFromIndexedDB(): Promise<boolean> {
        return this.localStorageService.get('adressen', this.dbStorageSA).then((value) => {
            if (value === false) {
                // Es konnte keinen Wert in der indexeddb für Objekte/Aufträge ermittelt werden
                return false;
            } else {
                // Die Aufträge konnten geladen werden
                if (value) {
                    this.dbAdressen = (value as ServiceAppModels.SA_Adress[]);
                }
                return true;
            }
        });
    }

    getAnzahlDokumente(serviceID: string): number {
        let counter = 0;
        // console.log('GetAnzahlDokumente: ' + serviceID)
        if (this.dbRapporte) {
            counter += this.dbRapporte.filter((x) => x.serviceID === serviceID).length;
        }
        if (this.dbBilder) {
            counter += this.dbBilder.filter((x) => x.serviceID === serviceID).length;
        }
        return counter;
    }

    async loadMasterDataFromIndexedDB(): Promise<boolean> {
        return this.localStorageService.get('stammdaten', this.dbStorageSA).then((value) => {
            if (value === false) {
                // Es konnte keinen Wert in der indexeddb für Stammdaten ermittelt werden
                return false;
            } else {
                // Die Stammdaten konnten geladen werden
                if (value) {
                    this.dbStammdaten = (value as ServiceAppModels.Stammdaten);
                    this.dbMitarbeiter = this.dbStammdaten.sA_Employees;
                    if (this.anwenderReaded) {
                        const anwender = this.userAuthService.getCurrentAuthenticatedUser();
                        this.employee = this.dbMitarbeiter.filter((mi) => mi.employeeID == anwender?.employeeID)[0];
                    }
                    this.dbMitarbeiterAnsaetze = this.dbStammdaten.sA_EmployeeRates;
                    this.dbKostenstellen = this.dbStammdaten.sA_CostCenter;
                    this.dbKostenstellenAnsaetze = this.dbStammdaten.sA_CostCenterRates;
                    this.dbSpesen = this.dbStammdaten.sA_Expenses;
                }
                return true;
            }
        });
    }

    async loadObjekteFromIndexedDB(callRegisteredFunctions: boolean): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService.get('objekte', this.dbStorageSA, false, []).then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für Objekte/Aufträge ermittelt werden
                    resolve(false);
                } else {
                    // Die Aufträge konnten geladen werden
                    if (value) {
                        this.sA_Objects = (value as ServiceAppModels.SA_Objekt[]);
                        this.dbTermine = this.sA_Objects;
                        this.localStorageService
                            .get('objektDokumente', this.dbStorageSA, false, [])
                            .then((value2) => {
                                if (value2 === false) {
                                    // Es konnte keinen Wert in der indexeddb für Objekte/Aufträge ermittelt werden
                                    resolve(false);
                                } else {
                                    // Die Aufträge konnten geladen werden
                                    if (value2) {
                                        this.dbDokumente = ((value2) as ServiceAppModels.SA_Document[]);
                                        this.localStorageService
                                            .get('terminDokumentZuord', this.dbStorageSA, false, [])
                                            .then((value3) => {
                                                if (value3 === false) {
                                                    // Es konnte keinen Wert in der indexeddb für Objekte/Aufträge ermittelt werden
                                                    resolve(false);
                                                } else {
                                                    // Die Aufträge konnten geladen werden
                                                    if (value3) {
                                                        this.sA_DocumentAssignments = (value3 as ServiceAppModels.SA_DocumentAssignment[]);
                                                        this.dbDokumentZuordnungen = this.sA_DocumentAssignments;
                                                        if (this.reloadAppointmentsFunc && callRegisteredFunctions) {
                                                            this.reloadAppointmentsFunc();
                                                        }
                                                        // Aufträge können immer neu geladen werden
                                                        if (this.reloadOrdersFunc) {
                                                            this.reloadOrdersFunc();
                                                        }

                                                        resolve(true);
                                                    }
                                                }
                                            }).catch(() => resolve(false));
                                    }
                                }
                            }).catch(() => resolve(false));
                    }
                }
            }).catch(() => resolve(false));
        });
    }

    async loadServicePositionenFromIndexedDB(): Promise<boolean> {
        const value = await this.localStorageService.get('servicePositionen', this.dbStorageSA, false, []);
        // Die Servicepositionen konnten geladen werden
        // 11.08.22/TH: Hier könnten WL-Positionen drin sein, welche noch nicht in die IndexDB-Tabelle ServicePos übertragen wurden
        if (value.length > 0) {
            this.sA_ServicePos = (value as ServiceAppModels.SA_ServicePos[]);
            await this.loadProductEntriesFromIndexedDB();
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            this.sA_ServicePos!.filter((x) => x.createdByWL === 1).forEach((pos) => {
                // Prüfen, ob die Position schon vorhanden ist => wenn nein, hinzufügen
                const orderPositionIndex = this.dbAuftragsProdukte?.findIndex((ap) => ap.servicePosID === pos.servicePosID);
                let mengeTotalCalc = this._zahlenFormatPipe.round(pos.amount! * pos.totalAmount!, 3);
                if (pos.createdByWL === 1 && pos.totalAmount) {
                    mengeTotalCalc = pos.totalAmount;
                }

                let totalPrice = pos.amount! * pos.saleprice!;
                if (mengeTotalCalc > 0.0) {
                    totalPrice = mengeTotalCalc * pos.saleprice!;
                }

                // Auf 0.05 Rappen runden
                totalPrice = this._zahlenFormatPipe.round5(totalPrice);

                if (orderPositionIndex == -1 && !pos.deletedInWL) {
                    const productEntry: ServiceAppModels.SA_ProductEntry = {
                        rowID: pos.fK_RowID!,
                        servicePosID: pos.servicePosID,
                        serviceID: pos.fK_ServiceID,
                        ausArtikelStamm: pos.productType == 2,
                        datum: pos.entryDate,
                        mitarbeiter: this.userAuthService.getCurrentAuthenticatedUser()!,
                        artikelnummer: pos.fK_ProductNo,
                        bezeichnung: pos.shortText,
                        anzahl: pos.amount!,
                        einheit: pos.unit,
                        preisProEinheit: pos.saleprice!,
                        laenge: pos.length!,
                        breite: pos.width!,
                        dicke: pos.thickness!,
                        menge: pos.totalAmount!,
                        mengeTotal: mengeTotalCalc,
                        kulanz: pos.goodWill === 1,
                        spesen: pos.productType == 5,
                        preisTotal: totalPrice,
                        deleted: false,
                        uploaded: true,
                        createdByWL: pos.createdByWL === 1
                    };
                    this.dbAuftragsProdukte!.push(productEntry);
                } else if (orderPositionIndex != undefined) {
                    const entry = this.dbAuftragsProdukte![orderPositionIndex];
                    if (!entry || entry?.modifiedInMobile) {
                        return;
                    }
                    const service = this.dbTermine!.find(service => service.serviceID == entry.serviceID);
                    if (service && new Date(service.startTime!) > today) {
                        if (pos.deletedInWL) {
                            this.dbAuftragsProdukte!.splice(orderPositionIndex, 1);
                        } else {
                            entry.anzahl = pos.amount!;
                            entry.laenge = pos.length!;
                            entry.breite = pos.width!;
                            entry.dicke = pos.thickness!;
                            entry.menge = pos.totalAmount!;
                            entry.mengeTotal = mengeTotalCalc;
                            entry.preisTotal = totalPrice;
                        }
                    }
                }
            });
            // Angepasste Positionen zum Termin in DB speichern
            await this.localStorageService.add('artikel', this.dbAuftragsProdukte, this.dbStorageSA);
            return true;
        }
        return true;
    }

    async loadSettingsFromIndexedDBWithFallBack(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('sa_config', this.dbStorageSA, false, false)
                .then((value) => {
                    if (value === false) {
                        this.getCurrentSAConfig().subscribe({
                            next: (conf) => {
                                this.sA_Config = conf;
                                this.localStorageService.add('sa_config', conf, this.dbStorageSA);
                            }, error: () => {
                                resolve(false);
                            }
                        });
                    } else {
                        if (value) {
                            this.sA_Config = (value as ServiceAppModels.SA_Config);
                            resolve(true);
                        }
                    }
                });
        });
    }

    getSA_Config(): ServiceAppModels.SA_Config | undefined {
        return this.sA_Config;
    }

    getKurzzeichenStunden(): string {
        // Wenn Mandant-Setting & Benutzer-Setting nicht gesetzt, dann Standard-Wert verwenden
        if (this.sA_Config?.kurzzeichenStunden || this.userAuthService.getCurrentAuthenticatedUser()?.sA_HoursText != '') {
            // Benutzer-Setting wenn vorhanden vorziehen
            if (this.userAuthService.getCurrentAuthenticatedUser()?.sA_HoursText != '') {
                return this.userAuthService.getCurrentAuthenticatedUser()!.sA_HoursText!;
            } else {
                return this.sA_Config!.kurzzeichenStunden;
            }
        } else {
            return 'h';
        }
    }

    // Gibt die gewünschte Anzahl Minuten zurück, mit welchen der Rapport gerundet werden soll.
    getRoundingTimes(): number {
        // switch 1 = 1 minute, 2 = 15 minute, 3 = 30 minute, 4 = 60 minute
        switch (this.userAuthService.getCurrentAuthenticatedUser()?.sA_RoundTimes) {
            case 1:
                return 1;
            case 2:
                return 15;
            case 3:
                return 30;
            case 4:
                return 60;
            default:
                // Standardmässig soll auf eine Minute gerundet werden.
                return 1;
        }
    }

    filterByDay(timeEntry: ServiceAppModels.SA_TimeEntry): boolean {
        // Hier werden die relevanten Datensätze aus der DB geladen
        return this._datumFormatPipe.transformOnlyDate(timeEntry.startDate) === this._datumFormatPipe.transformOnlyDate(this.dayDateValue) && !timeEntry.deleted;

    }

    loadTimeEntriesFromIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService.get('zeiten', this.dbStorageSA, false, []).then((value) => {
                this.dbAuftragsZeiten = ((value) as ServiceAppModels.SA_TimeEntry[]).filter((te) => !te.deleted);
                resolve(true);
            });
        });
    }

    loadTimeEntriesDayFromIndexedDB(dayDateValue: Date): Promise<boolean> {
        return new Promise((resolve) => {
            this.dayDateValue = dayDateValue;
            this.localStorageService.get('zeiten', this.dbStorageSA, false, []).then((value) => {
                this.dbAuftragsZeiten = ((value) as ServiceAppModels.SA_TimeEntry[]).filter(this.filterByDay.bind(this));
                resolve(true);
            });
        });
    }

    async updTimeEntryInIndexedDB(timeEntry: ServiceAppModels.SA_TimeEntry, servicePosID: string): Promise<boolean> {
        return await this.localStorageService
            .get('zeiten', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Zeiten in der DB vorhanden
                    this.dbAuftragsZeiten = [];
                } // Es sind schon Zeiten in der DB vorhanden
                else {
                    this.dbAuftragsZeiten = (value as ServiceAppModels.SA_TimeEntry[]);
                }

                if (timeEntry) {
                    // Neuen Eintrag in das Zeiten-Array speichern
                    const index = this.dbAuftragsZeiten.findIndex((x) => x.servicePosID == servicePosID);
                    this.dbAuftragsZeiten[index] = timeEntry;
                }

                // Angepasste Zeiten in DB speichern
                this.localStorageService.add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA);
                return true;
            });
    }

    async delTimEntryInIndexedDB(timeEntry: ServiceAppModels.SA_TimeEntry, servicePosID: string): Promise<void> {
        const value = await this.localStorageService.get('zeiten', this.dbStorageSA, false, []);
        // Bestehenden Eintrag im Zeit-Array überschreiben und als gelöscht markieren
        timeEntry.deleted = true;
        timeEntry.uploadedToServicePos = true; // Zeit muss zu ServicePos übermittelt werden
        timeEntry.uploadedToTimeControlEntries = true; // Zeit muss zu Zeitkontrolle übermittelt werden
        const index = value.findIndex((x) => x.servicePosID == servicePosID);
        value[index] = timeEntry;

        // Angepasste Zeiten in DB speichern
        this.dbAuftragsZeiten = (value as ServiceAppModels.SA_TimeEntry[]);
        await this.localStorageService.add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA);
        this.timesChanged$.next(value);
    }

    async loadTimeControlDaysFromIndexedDB(): Promise<boolean> {
        return this.localStorageService.get('zeitKontrolleTage', this.dbStorageSA).then((value) => {
            if (value === false) {
                // Es konnte keinen Wert in der indexeddb für Objekte/Aufträge ermittelt werden
                return false;
            } else {
                // Die Aufträge konnten geladen werden
                if (value) {
                    this.dbSA_TimeControlDays = ((value) as ServiceAppModels.SA_TimeControlDay[]);
                }
                return true;
            }
        });
    }

    getTimeControlDayState(datum: string): string {
        datum = this._datumFormatPipe.convertDateStringEN2DateStringCH(datum);
        const anwender = this.userAuthService.getCurrentAuthenticatedUser();
        if (this.dbSA_TimeControlDays && this.dbSA_TimeControlDays.length) {
            const index = this.dbSA_TimeControlDays.findIndex((x) => x.employeeID == anwender!.employeeID && x.datum == datum && x.mandator == anwender!.mandatorID);
            if (index >= 0) {
                if (this.dbSA_TimeControlDays[index].closed) {
                    return 'lock';
                } else {
                    return 'lock_open';
                }
            } else {
                return 'lock_open';
            }
        } else {
            return 'lock_open';
        }
    }

    async changeTimeControlDayState(datum: string): Promise<Boolean> {
        const anwender = this.userAuthService.getCurrentAuthenticatedUser();

        const timeControlDay = this.dbSA_TimeControlDays.filter((x) => x.datum === datum && x.employeeID === anwender!.employeeID && x.mandator === anwender!.mandatorID)[0];
        if (timeControlDay) {
            const index = this.dbSA_TimeControlDays.findIndex((x) => x.datum === datum && x.employeeID === anwender!.employeeID && x.mandator === anwender!.mandatorID);
            if (timeControlDay.closed) {
                if (this.currTimeEmployee && this.currTimeEmployee.dayAgainActive) {
                    const value = await this.svcMessageBox.askYesNoQuestion('MAIN_APP_DIALOG.REOPEN_DAY.msg', 'MAIN_APP_DIALOG.REOPEN_DAY.title');
                    if (value) {
                        this.dbSA_TimeControlDays[index].closed = false;
                        await this.localStorageService.add('zeitKontrolleTage', this.dbSA_TimeControlDays, this.dbStorageSA);
                        this.timesChanged$.next(undefined);
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    this.svcMessageBox.showError('Keine Berechtigungen für den Mitarbeiter vorhanden', 'Fehlende Berechtigungen');
                    return false;
                }
            } else {
                this.dbSA_TimeControlDays[index].closed = true;
                await this.localStorageService.add('zeitKontrolleTage', this.dbSA_TimeControlDays, this.dbStorageSA);
                this.timesChanged$.next(undefined);
                return true;
            }
        } else {
            // Eintrag machen, dass der Tag abgeschlossen ist
            this.dbSA_TimeControlDays.push({
                datum, closed: true, employeeID: this.userAuthService.getCurrentAuthenticatedUser()!.employeeID, mandator: this.userAuthService.getCurrentAuthenticatedUser()!.mandatorID
            });
            await this.localStorageService.add('zeitKontrolleTage', this.dbSA_TimeControlDays, this.dbStorageSA);
            this.timesChanged$.next(undefined);
            return true;
        }
    }

    async loadAnwenderFromIndexedDB(): Promise<boolean> {
        return this.localStorageService
            .get('anwender', 'SwissWebConfigDB')
            .then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für den Anwender ermittelt werden
                    return false;
                } else {
                    // Die Stammdaten konnten geladen werden
                    if (value) {
                        this.anwender = (value as BenutzerModels.Anwender);
                    }
                    return true;
                }
            });
    }

    async loadObjektAdressenFromIndexedDB(): Promise<boolean> {
        return this.localStorageService
            .get('objektAdressen', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für Objekte/Aufträge ermittelt werden
                    return false;
                } else {
                    // Die Aufträge konnten geladen werden
                    if (value) {
                        this.dbAuftragAdressen = (value as ServiceAppModels.SA_ObjectAdress[]);
                    }
                    return true;
                }
            });
    }

    async startDownloadData(): Promise<boolean> {
        try {
            this.helperService.pausePeriodicalChecks();
            if (this.doCancelSync) {
                return false;
            }
            this.showSyncMessage('Lade die Stammdaten vom Webservice.');
            this.showSyncProgressState(23);
            await new Promise((resolve, reject) => {
                this.getStammdaten().subscribe({
                    next: async (res) => {
                        this.showSyncMessage('Speichere die Stammdaten in den Speicher.');
                        this.showSyncProgressState(24);
                        await this.localStorageService.add('stammdaten', res, this.dbStorageSA);
                        resolve(true);
                    }, error: () => {
                        this.showSyncMessage('Fehler beim Laden der Stammdaten vom Webservice.');
                        reject();
                    }
                });
            });
            if (this.doCancelSync) {
                return false;
            }
            this.showSyncMessage('Lade die Artikel vom Webservice.');
            this.showSyncProgressState(25);
            await new Promise((resolve, reject) => {
                this.getProdukte().subscribe({
                    next: async (res1) => {
                        this.showSyncMessage('Speichere die Artikel vom Webservice.');
                        this.showSyncProgressState(26);
                        await this.localStorageService.add('produkte', res1, this.dbStorageSA);
                        resolve(true);
                    },
                    error: () => {
                        this.showSyncMessage('Fehler beim Laden der Artikel vom Webservice.');
                        reject();
                    }
                });
            });
            if (this.doCancelSync) {
                return false;
            }
            this.showSyncMessage('Lade die Aufträge vom Webservice.');
            this.showSyncProgressState(27);
            await this.downloadObjekte();
            this.showSyncMessage('Speichere die Aufträge in den Speicher.');
            this.showSyncProgressState(28);

            if (this.doCancelSync) {
                return false;
            }
            this.showSyncMessage('Lade die Aufträge des lokalen Speichers.');
            this.showSyncProgressState(29);

            const res = await this.loadObjekteFromIndexedDB(true);
            if (!res) {
                return false;
            }
            const res2 = await this.loadServicePositionenFromIndexedDB();
            if (!res2) {
                return false;
            }
            this.showSyncMessage('Lade die Dokumentverknüpfungen des lokalen Speichers.');
            this.showSyncProgressState(30);
            const documents = this.getDBObjektDokumente();
            this.dbDokumente = documents;
            // Alle Dateien herunterladen
            const syncDate = await this.localStorageService.get('last_SA_sync', 'SwissWebConfigDB', true);

            this.showSyncMessage('Lade die Dokumente vom Webservice in den lokalen Speicher.');
            this.showSyncProgressState(31);
            this.doSaveLocalPics = false;
            this.doSaveLocalCheckLists = false;

            const documentsSyncTask = await this.downloadDocuments();
            const downloadInfo = documentsSyncTask.getDownloadInformation();

            downloadInfo.totalDownloadedDocuments$.subscribe((totalDownloaded) => {
                const progressBarValue = 32 + (totalDownloaded * 68) / downloadInfo.totalDocuments;
                this.showSyncProgressState(progressBarValue);
                this.showSyncDocumentsState(totalDownloaded, downloadInfo.totalDocumentToDownload);
            });

            const syncDocumentResponse = await documentsSyncTask.downloadDocuments();

            if (this.doSaveLocalPics) {
                await this.localStorageService.add('bilder', this.dbBilder, this.dbStorageSA);
            }
            if (this.doSaveLocalCheckLists) {
                try {
                    await this.localStorageService.add('checklisten', this.dbChecklisten, this.dbStorageSA);
                } catch (err: any) {
                    // there is an unknown error in Safari when we reupdate the checklists to set uploaded here (its done previously without any issue but havent found out why and how)
                    // not updating the updated field also does not solve the issue here
                    // we assume it might be about using lastModifiedDate but it doesnt seem to be the issue (it still fails without it being set in the file)
                    // but quick testing didnt make it work when just exchanging lastModifiedDate with lastModified (also tried with an empty indexDB
                    // see todo https://swissjira.atlassian.net/browse/EN-4215
                    // TLDR: No clue we just catch and move on
                    // after we synced it seemed to be working again without getting this issue too so maybe a breaking change during ios update?
                    // wondering if this issue might be due to updating the checklists from within an async callback of an observable and how the browser handles that 	(ಥ﹏ಥ)
                    if (!this.platform.SAFARI || !err.message.includes('Error preparing Blob/File data to be stored in object store')) {
                        this.showSyncMessage('Checklisten Duplikate konnten nicht entfernt werden.');
                        throw err;
                    }
                }
            }

            this.showSyncMessage('Dokumente im lokalen Speicher abgespeichert. Lade Daten in die Service-App...');
            await this.loadDataForServiceApp();
            this.showSyncMessage('Neue Daten in die Service-App geladen.');
        } finally {
            this.helperService.restartPeriodicalChecks();
        }
        return true;
    }

    loadProductEntriesFromIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('artikel', this.dbStorageSA)
                .then((value) => {
                    if (!value) {
                        // Es sind keine Positionen zum Termin in der DB vorhanden
                        if (this.svcSwissSettings.getWebConfig().debugMode) {
                            console.log('Keine Positionen aus der DB geladen...');
                        }
                        this.dbAuftragsProdukte = [];
                        resolve(true);
                    } // Es sind schon Positionen zum Termin in der DB vorhanden
                    else {
                        if (this.svcSwissSettings.getWebConfig().debugMode) {
                            console.log('Erfasste Positionen aus der DB geladen...');
                        }
                        this.dbAuftragsProdukte = ((value) as ServiceAppModels.SA_ProductEntry[]).filter((p) => !p.deleted);
                        resolve(true);
                    }
                });
        });
    }

    getStammdaten(): Observable<ServiceAppModels.Stammdaten> {
        const url = this.webServiceConfig!.saUrl;
        return this.http.get<ServiceAppModels.Stammdaten>(url + '/Stammdaten?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getProdukte(): Observable<ServiceAppModels.Produkt[]> {
        const url = this.webServiceConfig!.saUrl;
        return this.http.get<ServiceAppModels.Produkt[]>(url + '/Produkte?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    async downloadObjekte(): Promise<boolean> {
        const res: any = await this.getObjektKategorien();
        if (this.svcSwissSettings.getWebConfig().debugMode) {
            console.log('Heruntergeladene Objektkategorien', res);
        }

        await this.getObjectOrders();
        const p1 = new Promise<boolean>(((resolve, reject) => {
            this.refreshServicePositions().subscribe({
                next: (res) => {
                    if (!res) {
                        console.log('Keine Service Positionen zum neuladen gefunden');
                        resolve(false);
                        return;
                    }
                    this.getServicePositionen(false).subscribe({
                        next: async (res: ServiceAppModels.SA_ServicePos[]) => {
                            await this.localStorageService.add('servicePositionen', res, this.dbStorageSA);
                            this.sA_ServicePos = res;
                            this.loadServicePositionenFromIndexedDB().then(() => {
                                resolve(true);
                            });
                        },
                        error: (err) => {
                            console.error('Fehler beim laden der Service Positionen');
                            console.error(err);
                            resolve(false);
                        }
                    });
                },
                error: (err) => {
                    console.log(err);
                    resolve(false);
                }
            });
        }));

        const p2 = new Promise<boolean>((resolve, reject) => {
            this.getAlleAdressen().subscribe({
                next: (saAddresses) => {
                    this.localStorageService.add('adressen', saAddresses, this.dbStorageSA);
                    this.getObjektAdressen().subscribe({
                        next: async (saObjectAddresses) => {
                            this.localStorageService.add('objektAdressen', saObjectAddresses, this.dbStorageSA);
                            const syncConfig: ServiceAppModels.SynchronizationPeriod = await this.localStorageService.get('service-app-mobile-config', 'SwissWebConfigDB', false, ServiceAppModels.DEFAULT_SYNC_PERIOD)
                            this.getObjektDokumenteFromTo(syncConfig).subscribe({
                                next: (saDocs) => {
                                    this.localStorageService.add('objektDokumente', saDocs, this.dbStorageSA);
                                    this.getTerminDokumente().subscribe({
                                        next: (saDocAssignments) => {
                                            this.localStorageService.add('terminDokumentZuord', saDocAssignments, this.dbStorageSA);
                                            resolve(true);
                                        },
                                        error: (err) => {
                                            console.error(err);
                                            resolve(false);
                                        }
                                    });
                                },
                                error: (err) => {
                                    console.error(err);
                                    resolve(false);
                                }
                            });
                        },
                        error: (err) => {
                            console.log(err);
                            resolve(false);
                        }
                    });
                },
                error: (err) => {
                    console.log(err);
                    resolve(false);
                }
            });
        });
        return (await Promise.all([p1, p2])).reduce(
            (accumulator, currentValue) => accumulator && currentValue,
            true
        );
    }

    getObjektKategorien(): Promise<ServiceAppModels.SA_ObjektKategorie[]> {
        const url = this.webServiceConfig!.saUrl;

        return new Promise(resolve => {
            this.http.get<ServiceAppModels.SA_ObjektKategorie[]>(url + '/ObjektKategorien?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).subscribe({
                next: async (res) => {
                    await this.localStorageService.add('objektKategorien', res, this.dbStorageSA);
                    resolve(res);
                }
            });
        });
    }

    async getObjectOrders(): Promise<any> {
        const syncConfig: ServiceAppModels.SynchronizationPeriod = await this.localStorageService.get('service-app-mobile-config', 'SwissWebConfigDB', false, ServiceAppModels.DEFAULT_SYNC_PERIOD);

        const url = this.webServiceConfig!.saUrl +
            '/ObjektAuftraegeFromTo?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID
            + '&DaysInPast=' + (syncConfig.syncInPast || ServiceAppModels.DEFAULT_SYNC_PERIOD.syncInPast)
            + '&DaysInFuture=' + (syncConfig.syncInFuture || ServiceAppModels.DEFAULT_SYNC_PERIOD.syncInFuture)
            + '&SendUnfinishedOrders=' + ((syncConfig.notClosedOrder !== undefined) ? syncConfig.notClosedOrder : ServiceAppModels.DEFAULT_SYNC_PERIOD.notClosedOrder);
        const header = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
                timeout: '300000'
            })
        };

        return new Promise(resolve => {
            this.http.get<ServiceAppModels.SA_Objekt[]>(url, header).subscribe({
                next: async (res) => {
                    const diff = (await this.localStorageService.get<ServiceAppModels.SA_Objekt[]>('objekte', this.dbStorageSA, false, [])).filter((e) => !res.find(o => e.objectID == o.objectID && e.serviceID == o.serviceID && e.rowID == o.rowID));

                    // handle objectOrders that got deleted in Navi
                    if (diff.length > 0) {

                        const trackedTimes = await this.localStorageService.get<ServiceAppModels.SA_TimeEntry[]>('zeiten', this.dbStorageSA, false, []);
                        const articles = (await this.localStorageService.get<ServiceAppModels.SA_ProductEntry[]>('artikel', this.dbStorageSA, false, [])).filter(a => !a.uploaded);
                        const saPositions = (await this.localStorageService.get<ServiceAppModels.SA_ServicePos[]>('servicePos', this.dbStorageSA, false, []));
                        const costCenters = this.dbStammdaten?.sA_CostCenter || [];
                        const standardObject = (await this.localStorageService.get<{ note4: string, objDescription: string, objectID: string, objectNumber: string }>('standardObjekt', this.dbStorageSA, false));

                        const savedServicePos: SA_ServicePos[] = [];

                        diff.forEach(o => {
                            const objectArticles = articles.filter(a => o.serviceID == a.serviceID);
                            const objectTimes = trackedTimes.filter(a => o.serviceID == a.serviceID);

                            if (objectTimes.length > 0 || objectArticles.length > 0) {
                                this.svcMessageBox.showErrorOK('Einsatz ' + o.objectNumber + ' ' + o.objDescription + ' ' + o.startTime + ' wurde gelöscht.\n' + objectArticles.length + ' Artikel und ' + objectTimes.length + ' Zeiten wurden auf das Standardobjekt ' + standardObject.objectNumber + ' ' + standardObject.objDescription + ' gebucht', 'Einsatz mit Buchungen wurde gelöscht');
                            }

                            objectArticles.forEach(oA => {
                                const sa_OA: ServiceAppModels.SA_ServicePos = {
                                    deleted: false,
                                    entryDate: oA.datum,
                                    fK_ProductNo: oA.artikelnummer,
                                    mandator: o.mandator,
                                    shortText: oA.bezeichnung,
                                    transferred: false,
                                    unit: oA.einheit,
                                    uploaded: false,
                                    servicePosID: oA.servicePosID,
                                    fK_ObjectID: o.objectID,
                                    fK_EmployeeNo: oA.mitarbeiter.employeeID,
                                    fK_ServiceID: oA.serviceID,
                                    fK_RowID: oA.rowID,
                                    amount: oA.anzahl,
                                    saleprice: oA.preisProEinheit,
                                    goodWill: oA.kulanz ? 0 : 1,
                                    length: oA.laenge,
                                    width: oA.breite,
                                    thickness: oA.dicke,
                                    totalAmount: oA.mengeTotal,
                                    createdByWL: oA.createdByWL ? 1 : 0
                                };
                                savedServicePos.push(sa_OA);
                            });

                            objectTimes.forEach(oT => {
                                const costCenter = costCenters.find(cc => cc.costCenterID == oT.costCenterID);

                                let regieAnsatz = 0;
                                if (oT.costCenterID) {
                                    regieAnsatz = this.getDBKostenstellenRegieAnsatz(oT!.startDate!, oT.costCenterID);
                                }

                                oT.employeeIDS.forEach(employeeID => {
                                    const hourlyRate = this.getDBMitarbeiterAnsatz(oT!.startDate!, employeeID);

                                    const saObjectTime: ServiceAppModels.SA_ServicePos = {
                                        amount: oT.duration,
                                        deleted: false,
                                        entryDate: oT.startDate || new Date(),
                                        fK_CostCenterID: oT.costCenterID,
                                        fK_EmployeeNo: employeeID,
                                        fK_ObjectID: standardObject.objectID,
                                        fK_ProductNo: '' + (costCenter?.costCenterID || 0),
                                        fK_ServiceID: oT.serviceID || '',
                                        goodWill: oT.goodWill ? 1 : 0,
                                        mandator: o.mandator,
                                        productType: costCenter?.costCenterID,
                                        saleprice: hourlyRate + regieAnsatz,
                                        servicePosID: oT.servicePosID,
                                        shortText: oT.activityDescription,
                                        transferred: false,
                                        totalAmount: oT.duration,
                                        unit: 'h',
                                        uploaded: false
                                    };
                                    savedServicePos.push(saObjectTime);

                                    if (oT.expenseID) {
                                        const saExpense: ServiceAppModels.SA_ServicePos = {
                                            amount: oT.expenseAmount,
                                            deleted: false,
                                            entryDate: oT.startDate || new Date(),
                                            expenseServicePosID: oT.servicePosID,
                                            fK_CostCenterID: oT.costCenterID,
                                            fK_EmployeeNo: employeeID,
                                            fK_ObjectID: standardObject.objectID,
                                            fK_ProductNo: '',
                                            fK_ServiceID: oT.serviceID || '',
                                            goodWill: oT.goodWill ? 1 : 0,
                                            mandator: o.mandator,
                                            productType: costCenter?.costCenterID,
                                            saleprice: oT.expensePrice,
                                            servicePosID: oT.servicePosID,
                                            shortText: oT.activityDescription,
                                            transferred: false,
                                            totalAmount: oT.expenseAmount,
                                            unit: oT.expenseUnit || '',
                                            uploaded: false
                                        };
                                        savedServicePos.push(saExpense);
                                    }
                                });
                            });

                        });
                        const result = await this.doUploadServicePos(savedServicePos);
                        (result as any).servicePosResultList.forEach(res => {
                            const pos = savedServicePos.findIndex(sp => sp.servicePosID == res.servicePosID);
                            if (pos > -1 && !res.hasError) {
                                savedServicePos[pos].uploaded = true;
                                savedServicePos[pos].transferred = true;
                            }
                        });

                        await this.localStorageService.add('servicePos', saPositions.concat(savedServicePos), this.dbStorageSA);
                    }

                    await this.localStorageService.add('objekte', res, this.dbStorageSA);
                    resolve(res);
                }, error: (err) => {
                    console.error(err);
                    resolve([]);
                }
            });
        });
    }

    getAlleAdressen(): Observable<ServiceAppModels.SA_Adress[]> {
        const url = this.webServiceConfig!.saUrl;
        return this.http.get<ServiceAppModels.SA_Adress[]>(url + '/Adressen?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjektAdressen(): Observable<ServiceAppModels.SA_ObjectAdress[]> {
        const url = this.webServiceConfig!.saUrl;
        return this.http.get<ServiceAppModels.SA_ObjectAdress[]>(url + '/ObjektAdressen?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjektDokumente(withFileModifiedInfo = true): Observable<ServiceAppModels.SA_Document[]> {
        const url = this.webServiceConfig!.saUrl;
        return this.http.get<ServiceAppModels.SA_Document[]>(url + '/ObjektDokumente?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID + '&withFileModifiedInfo=' + withFileModifiedInfo, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjektDokumenteFromTo(syncConfig: ServiceAppModels.SynchronizationPeriod = DEFAULT_SYNC_PERIOD, withFileModifiedInfo = true): Observable<ServiceAppModels.SA_Document[]> {
        if (!this.webServiceConfig) {
            throw new Error('Webservice Config not set');
        }
        const header = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
                timeout: '300000'
            })
        };

        const url = this.webServiceConfig.saUrl +
            '/ObjektDokumenteFromTo?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID +
            '&withFileModifiedInfo=' + withFileModifiedInfo
            + '&DaysInPast=' + (syncConfig.syncInPast || ServiceAppModels.DEFAULT_SYNC_PERIOD.syncInPast)
            + '&DaysInFuture=' + (syncConfig.syncInFuture || ServiceAppModels.DEFAULT_SYNC_PERIOD.syncInFuture)
            + '&SendUnfinishedOrders=' + ((syncConfig.notClosedOrder !== undefined) ? syncConfig.notClosedOrder : ServiceAppModels.DEFAULT_SYNC_PERIOD.notClosedOrder);

        return this.http.get<ServiceAppModels.SA_Document[]>(url, header);
    }

    getTerminDokumente(): Observable<ServiceAppModels.SA_DocumentAssignment[]> {
        if (!this.webServiceConfig) {
            throw new Error('Webservice Config is not set');
        }

        const url = this.webServiceConfig.saUrl + '/TerminDokumentZuordnungen?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID;
        const header = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
                timeout: '300000'
            })
        };
        return this.http.get<ServiceAppModels.SA_DocumentAssignment[]>(url, header);
    }

    getServicePositionen(refreshSAPositions = true): Observable<ServiceAppModels.SA_ServicePos[]> {
        if (!this.webServiceConfig) {
            throw new Error();
        }
        const url = this.webServiceConfig.saUrl;
        return this.http.get<ServiceAppModels.SA_ServicePos[]>(url + '/ServicePositionen?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID + '&refreshSAPositions=' + refreshSAPositions, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getRapportHeaderLogo(filePath: string): Observable<any> {
        const url = this.webServiceConfig!.saUrl;

        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        return this.http.get<any>(url + '/DownloadRapportLogo?isHeader=true&filePath=' + filePath, {headers, responseType: 'blob' as 'json'});
    }

    getChecklisteTemplate(mandant: SwissAppMandant): Promise<ServiceAppModels.CheckListeTemplate> {
        const url = this.webServiceConfig!.saUrl;
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        return new Promise((resolve, reject) => {
            this.http.get<any>(url + '/DownloadChecklistDatei?filePath=' + mandant.checkListePath, {headers, responseType: 'blob' as 'json'}).subscribe({
                next: async (res) => {
                    const checklistTemplates = await this.localStorageService.get('checkListenTemplates', SWISS_SERVICE_APP_DB_STORE_NAME, false, []);
                    const mandantCheckliste: ServiceAppModels.CheckListeTemplate = {
                        mandatorID: mandant.mandatorID, pdfFileTemplate: res,
                    };
                    const index = checklistTemplates.findIndex(checklistTemplate => checklistTemplate.mandatorID == mandant.mandatorID);
                    if (index > -1) {
                        checklistTemplates[index] = mandantCheckliste;
                    } else {
                        checklistTemplates.push(mandantCheckliste);
                    }
                    await this.localStorageService.add('checkListenTemplates', checklistTemplates, SWISS_SERVICE_APP_DB_STORE_NAME);

                    resolve(mandantCheckliste);
                }, error: async (err) => {
                    const checklist = (await this.localStorageService.get('checkListenTemplates', SWISS_SERVICE_APP_DB_STORE_NAME, false, [])).find(checkListe => checkListe.mandatorID === mandant.mandatorID);
                    if (checklist) {
                        resolve(checklist);
                    } else {
                        this.svcMessageBox.showError(err, 'Checklisten Template nicht verfügbar');
                        reject();
                    }
                }
            });
        });
    }

    async getChecklisten() {
        await this.localStorageService.get('checklisten', this.dbStorageSA, false, []).then((value) => {
            this.dbChecklisten = (value as ServiceAppModels.SA_ChecklistFile[]);
        });
    }

    loadChecklistenFromIndexedDB() {
        return new Promise((resolve) => {
            this.localStorageService.get('checklisten', this.dbStorageSA, false, []).then((value) => {
                // Es sind keine Rapporte in der DB vorhanden
                this.dbChecklisten = value;
                resolve(true);
            });
        });
    }

    async loadDokumenteFromIndexedDB(): Promise<any[]> {
        this.dbDokumenteToUpload = await this.localStorageService.getAllKeys(this.dbStorageSA_Doku);
        return (this.dbDokumenteToUpload);
    }

    getChecklisteByDocumentID(documentID: string): ServiceAppModels.SA_ChecklistFile {
        return this.dbChecklisten.filter((x) => x.documentID === documentID)[0];
    }

    // Die folgende Funkion ermittelt nur Checklisten, welche noch nicht übermittelt wurden
    getChecklistenByServiceID(serviceID: string): ServiceAppModels.SA_ChecklistFile[] {
        return this.dbChecklisten.filter((x) => x.serviceID === serviceID);
    }

    // Diese Funktion prüft, ob auf dem Auftrag eine Checkliste vorhanden ist
    existsChecklistenByServiceID(serviceID: string): boolean {
        let result = this.dbChecklisten.filter((x) => x.serviceID === serviceID).length > 0;
        if (result) {
            return true;
        } else {
            if (this.dbDokumentZuordnungen && this.dbDokumentZuordnungen.length > 0 && this.dbDokumente && this.dbDokumente.length > 0) {
                const terminDokus = this.dbDokumentZuordnungen.filter((x) => x.fK_ServiceID === serviceID);
                if (terminDokus.length > 0) {
                    for (const td of terminDokus) {
                        const doc = this.dbDokumente!.filter(d => d.documentID === td.documentID)[0];
                        if (doc.sA_TransDocType === 2) /* Checkliste */ {
                            result = true;
                            break;
                        }
                    }
                }
            }
        }
        return result;
    }

    async loadChecklistFromIndexedDB(): Promise<boolean> {
        return this.localStorageService
            .get('checkListenTemplates', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für Checklisten ermittelt werden
                    return false;
                } else {
                    // Die Checkliste(n) konnten geladen werden
                    if (value) {
                        this.dbChecklistenTemplates = (value as ServiceAppModels.CheckListeTemplate[]);
                    }
                    return true;
                }
            });
    }

    existsCheckliste(mandatorID: string): boolean {
        if (this.dbChecklistenTemplates && this.dbChecklistenTemplates.length > 0) {
            const checkliste = this.dbChecklistenTemplates.filter((chkLst) => chkLst.mandatorID === mandatorID);
            if (checkliste && checkliste.length > 0) {
                return true;
            }
        }
        return false;
    }

    getFileByDocumentID(documentID: string, documentSource: DokumentModels.DokumentSource): Promise<ServiceAppModels.SA_BaseFile | undefined> {
        return new Promise((resolve, reject) => {
            if (documentSource === DokumentModels.DokumentSource.SA_DBChecklisten) {
                resolve(this.dbChecklisten.filter((x) => x.documentID === documentID)[0]);
            } else if (documentSource === DokumentModels.DokumentSource.SA_DBRapporte) {
                resolve(this.dbRapporte.filter((x) => x.documentID === documentID)[0]);
            } else {
                this.localStorageService
                    .get(documentID, this.dbStorageSA_Doku)
                    .then((value) => {
                        if (value === false) {
                            // Es sind keine Zeiten in der DB vorhanden
                            reject(undefined);
                        } else {
                            resolve(value as ServiceAppModels.DokumentDBFile);
                        }
                    });
            }
        });
    }

    setTerminByServiceID(serviceID: string) {
        this.currTermin = this.dbTermine?.filter((t) => t.serviceID === serviceID)[0];
    }

    setTermin(termin: ServiceAppModels.SA_Objekt) {
        this.currTermin = termin;
    }

    setServiceComplete(termin: ServiceAppModels.SA_Objekt) {
        if (this.dbTermine) {
            // Alle Array-Index ermitteln mit gleicher Service-Id
            const termine = Array.from(this.dbTermine);
            termine.forEach((t, index) => {
                if (t.serviceID == termin.serviceID && !t.serviceComplete) {
                    this.dbTermine![Number(index)].serviceComplete = 1;
                    this.dbTermine![Number(index)].modified = true;
                }
            });
            this.saveDBTermineInIndexedDB();
        }
    }

    public async removeTermin() {
        this.currTermin = undefined;
        await this.localStorageService.delete('last_used_appointment', this.dbStorageSA);
        this.timesChanged$.next(undefined);
    }

    getTermin(): ServiceAppModels.SA_Objekt {
        return this.currTermin!;
    }

    getTerminByServiceID(serviceID: string): ServiceAppModels.SA_Objekt | undefined {
        if (this.dbTermine) {
            return this.dbTermine?.filter((t) => t.serviceID === serviceID)[0];
        } else {
            return undefined;
        }
    }

    getObjektByObjektID(objectID: string): ServiceAppModels.SA_Objekt | undefined {
        if (this.dbTermine) {
            return this.dbTermine?.filter((t) => t.objectID === objectID && t.serviceID === null)[0];
        } else {
            return undefined;
        }
    }

    /* Erfasstes Produkt speichern */
    async saveProdToIndexedDB(productEntry: ServiceAppModels.SA_ProductEntry): Promise<boolean> {
        return await this.localStorageService
            .get('artikel', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Positionen zum Termin in der DB vorhanden
                    this.dbAuftragsProdukte = [];
                } // Es sind schon Positionen zum Termin in der DB vorhanden
                else {
                    this.dbAuftragsProdukte = (value as ServiceAppModels.SA_ProductEntry[]);
                }

                if (productEntry)
                    // Neuen Eintrag in das Produkte-Array (Artikel) speichern
                {
                    this.dbAuftragsProdukte.push(productEntry);
                }

                // Angepasste Positionen zum Termin in DB speichern
                this.localStorageService.add('artikel', this.dbAuftragsProdukte, this.dbStorageSA);
                return true;
            });
    }

    // Bestehendes Produkt mutieren
    async updProdInIndexedDB(productEntry: ServiceAppModels.SA_ProductEntry): Promise<boolean> {
        const value = await this.localStorageService.get('artikel', this.dbStorageSA, false, []);
        this.dbAuftragsProdukte = (value as ServiceAppModels.SA_ProductEntry[]);

        // Bestehenden Eintrag im Produkte-Array überschreiben
        productEntry.uploaded = false; // Produkt muss übermittelt werden
        if (this.svcSwissSettings.getWebConfig().debugMode) {
            console.log('Edit-Index', this.dbAuftragsProdukte.findIndex((item) => item.servicePosID === productEntry.servicePosID), productEntry, this.dbAuftragsProdukte);
        }
        this.dbAuftragsProdukte[this.dbAuftragsProdukte.findIndex((item) => item.servicePosID === productEntry.servicePosID)] = productEntry;

        // Angepasstes Produkt zum Termin in DB speichern
        await this.localStorageService.add('artikel', this.dbAuftragsProdukte, this.dbStorageSA);
        return true;
    }

    // Bestehendes Produkt löschen
    async delProdInIndexedDB(productEntry: ServiceAppModels.SA_ProductEntry): Promise<boolean> {
        return await this.localStorageService
            .get('artikel', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Zeiten in der DB vorhanden
                    this.dbAuftragsProdukte = [];
                } // Es sind schon Zeiten in der DB vorhanden
                else {
                    this.dbAuftragsProdukte = (value as ServiceAppModels.SA_ProductEntry[]);
                }

                if (productEntry) {
                    // Bestehenden Eintrag im Produkte-Array überschreiben und als gelöscht markieren
                    productEntry.deleted = true;
                    productEntry.uploaded = false; // Produkt muss übermittelt werden
                    this.dbAuftragsProdukte[this.dbAuftragsProdukte.findIndex((item) => item.servicePosID === productEntry.servicePosID)] = productEntry;
                }

                // Angepasste Zeiten in DB speichern
                this.localStorageService.add('artikel', this.dbAuftragsProdukte, this.dbStorageSA);
                return true;
            });
    }

    async loadArticlesFromIndexedDB(): Promise<boolean> {
        return this.localStorageService
            .get('produkte', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für Produkte ermittelt werden
                    return false;
                } else {
                    // Die Produkte konnten geladen werden
                    if (value) {
                        this.dbProdukte = (value as ServiceAppModels.Produkt[]);
                    }
                    return true;
                }
            });
    }

    // Ausgewählten Termin aus Agenda in IndexedDB speichern
    // (Beim nächsten Auftruf der Agenda ist dieser gleich aktiv)
    saveChoosedAppointmentToDB(rowID: string) {
        this.localStorageService.add('last_used_appointment', rowID, this.dbStorageSA);
    }

    getChoosedAppointmentFromDB(): Promise<string> {
        let rowID = '';
        return this.localStorageService
            .get('last_used_appointment', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für einen zuletzt ausgewählten Termin ermittelt werden
                    return rowID;
                } else {
                    // Ein Termin (Service-ID) konnte ermittelt werden.
                    if (value) {
                        rowID = String(value);
                    }
                    return rowID;
                }
            });
    }

    getAuftragsAdresse(objectID: string, adressTyp: GlobalModels.AdressTypes): ServiceAppModels.SA_Adress | undefined {
        const auftragAdressen = this.getAuftragAdressen();
        const auftragAdresse: ServiceAppModels.SA_Adress = ServiceAppModels.EmptySA_Adress();
        let fK_AdressID = '';
        if (auftragAdressen.length > 0) {
            const adresse = auftragAdressen.filter((a) => a.fK_ObjectID == objectID && a.fK_AddressTypeNumber == adressTyp)[0];
            if (adresse) {
                fK_AdressID = adresse.fK_AdressID;
                if (fK_AdressID) {
                    return this.getAdressen().filter((y) => y.adressID == fK_AdressID)[0];
                }
            }
        }
        return auftragAdresse;
    }

    getAuftragsKontaktAdresse(objectID: string, adressTyp: GlobalModels.AdressTypes): ServiceAppModels.SA_Adress | undefined {
        const auftragAdressen = this.getAuftragAdressen();
        const kontaktAdresse: ServiceAppModels.SA_Adress = ServiceAppModels.EmptySA_Adress();
        let fK_ContactID = '';
        if (auftragAdressen.filter((a) => a.fK_ObjectID == this.currTermin!.objectID && a.fK_AddressTypeNumber == 1)[0]) {
            const adresse = auftragAdressen.filter((a) => a.fK_ObjectID == this.currTermin!.objectID && a.fK_AddressTypeNumber == adressTyp)[0]; /* Kontaktadresse */
            if (adresse) {
                fK_ContactID = adresse.fK_ContactID;
                if (this.svcSwissSettings.getWebConfig().debugMode) {
                    console.log('fK_ContactID', fK_ContactID);
                }
                if (fK_ContactID) {
                    return this.getAdressen().filter((y) => y.adressID == fK_ContactID)[0];
                }
            }
        }
        return kontaktAdresse;
    }

    getAdresseDetail(objectID: string, adressType: GlobalModels.AdressTypes): ServiceAppModels.SA_RechnungsAdresseDetail {
        const sa_AdresseDetail: ServiceAppModels.SA_RechnungsAdresseDetail = ServiceAppModels.EmptySA_KundeDetail();
        const currAdress = this.getAuftragsAdresse(objectID, adressType);

        if (currAdress) {
            sa_AdresseDetail.adressID = currAdress.adressID;

            let kundeName = '';
            if (currAdress.firstName) {
                kundeName = currAdress.firstName + ' ';
            }
            kundeName += currAdress.lastName;
            sa_AdresseDetail.name = kundeName;

            let kundeAdr = '';
            if (currAdress.street1) {
                kundeAdr += currAdress.street1;
            }
            if (currAdress.zip && currAdress.city) {
                if (kundeAdr) {
                    kundeAdr += ', ';
                }
                kundeAdr += currAdress.zip + ' ' + currAdress.city;
            }
            sa_AdresseDetail.adresse = kundeAdr;
            sa_AdresseDetail.phone1 = currAdress.phone01;
            sa_AdresseDetail.phone2 = currAdress.phone02;
            sa_AdresseDetail.mobile1 = currAdress.mobile;
            sa_AdresseDetail.mobile2 = currAdress.mobile2;
            sa_AdresseDetail.mail1 = currAdress.email;
            sa_AdresseDetail.mail2 = currAdress.email2;
        }

        return sa_AdresseDetail;
    }

    getAdresseKontaktDetail(objectID: string, adressType: GlobalModels.AdressTypes): ServiceAppModels.SA_RechnungsAdresseDetail {
        const sa_AdresseDetail: ServiceAppModels.SA_RechnungsAdresseDetail = ServiceAppModels.EmptySA_KundeDetail();
        const currAdress = this.getAuftragsKontaktAdresse(objectID, adressType);

        if (currAdress) {
            sa_AdresseDetail.adressID = currAdress.adressID;

            let kundeName = '';
            if (currAdress.firstName) {
                kundeName = currAdress.firstName + ' ';
            }
            kundeName += currAdress.lastName;
            sa_AdresseDetail.name = kundeName;

            let kundeAdr = '';
            if (currAdress.street1) {
                kundeAdr += currAdress.street1;
            }
            if (currAdress.zip && currAdress.city) {
                if (kundeAdr) {
                    kundeAdr += ', ';
                }
                kundeAdr += currAdress.zip + ' ' + currAdress.city;
            }
            sa_AdresseDetail.adresse = kundeAdr;
            sa_AdresseDetail.phone1 = currAdress.phone01;
            sa_AdresseDetail.phone2 = currAdress.phone02;
            sa_AdresseDetail.mobile1 = currAdress.mobile;
            sa_AdresseDetail.mobile2 = currAdress.mobile2;
            sa_AdresseDetail.mail1 = currAdress.email;
            sa_AdresseDetail.mail2 = currAdress.email2;
        }

        return sa_AdresseDetail;
    }

    getAuftragsKundenAdresseZeile(objectID: string): string {
        let kunde = '';
        const currClientAdress = this.getAuftragsAdresse(objectID, GlobalModels.AdressTypes.Kunde);
        if (currClientAdress) {
            if (currClientAdress.firstName) {
                kunde = currClientAdress.firstName + ' ';
            }
            kunde += currClientAdress.lastName;
            if (currClientAdress.street1) {
                kunde += ', ' + currClientAdress.street1;
            }
            if (currClientAdress.zip && currClientAdress.city) {
                kunde += ', ' + currClientAdress.zip + ' ' + currClientAdress.city;
            }
        }
        return kunde;
    }

    saveTerminDetails(terminDetails: ServiceAppModels.SA_AppointmentDetails) {
        if (this.dbTermine) {
            const index = this.dbTermine.findIndex((o) => o.rowID === terminDetails.rowID);
            const termin = this.dbTermine[index];
            if (termin) {
                if (terminDetails.datum instanceof moment) {
                    terminDetails.datum = (terminDetails.datum as any).toDate();
                }
                // Die Terminmutation (Datum/Zeit) muss sofort übermittelt werden, wenn etwas geändert hat (Kalender-Eintrag wurde angepasst!!)
                if (terminDetails.dateOrTimeModified) {
                    const datum = new Date(terminDetails.datum.getFullYear(), terminDetails.datum.getMonth(), terminDetails.datum.getDate(), 0, 0, 0);

                    // Korrektur Zeitzone
                    const offSetMin = -1 * datum.getTimezoneOffset();
                    const offSetHour = offSetMin / 60;
                    datum.setMinutes(offSetMin); // Korrektur der Minuten wegen der Zeitzone

                    // Start-Zeit
                    const hours = Number(terminDetails.startzeit.substring(0, 2));
                    const minutes = Number(terminDetails.startzeit.substring(3, 5));
                    const datumVon = new Date(terminDetails.datum.getFullYear(), terminDetails.datum.getMonth(), terminDetails.datum.getDate(), hours + offSetHour, minutes, 0);

                    // End-Zeit
                    const hoursBis = Number(terminDetails.endzeit.substring(0, 2));
                    const minutesBis = Number(terminDetails.endzeit.substring(3, 5));
                    const datumBis = new Date(terminDetails.datum.getFullYear(), terminDetails.datum.getMonth(), terminDetails.datum.getDate(), hoursBis + offSetHour, minutesBis, 0);

                    if (datumVon >= datumBis) {
                        return this.svcMessageBox.showError('Startzeit muss vor Endzeit liegen', '!! FEHLER !!');
                    }

                    const assignment: ServiceAppModels.Assignment = {
                        employeeID: Number(termin.fK_EmployeeID), mandator: termin.mandator, rowID: termin.rowID, serviceID: terminDetails.serviceID, objectID: termin.objectID, day: datum, startTime: datumVon, endTime: datumBis,
                    };
                    this.updateEmpWorkPlanEntry(assignment).subscribe({
                        next: () => {
                            termin.startTime = this._datumFormatPipe.addTimeToDate(terminDetails.datum, terminDetails.startzeit);
                            termin.endTime = this._datumFormatPipe.addTimeToDate(terminDetails.datum, terminDetails.endzeit);
                            termin.notes = terminDetails.note;
                            termin.additionalWork = terminDetails.additionalWork;
                            termin.sA_Memo_Add = terminDetails.sa_Memo_Add;
                            termin.modified = true;
                            this.dbTermine![index] = termin;
                            this.saveDBTermineInIndexedDB();
                            // Der Agena mitteilen, dass dies der zuletzt verwendete Termin ist
                            this.saveChoosedAppointmentToDB(assignment.rowID);
                        }, error: (error) => {
                            // Im Fehlerfall wird das Datum/Zeitraum nicht aktualisiert
                            // termin.notes = terminDetails.note; 21.09.22/TH: Gemäss Marco Notiz nicht anpassen, wenn Live-Sync nicht geht!!
                            termin.additionalWork = terminDetails.additionalWork;
                            // wenn serviceIDGenerieren nicht true ist, dann könnten noch andere Einsätze mit der gleichen Service-ID vorhanden sein,
                            // wo die zusätzliche Arbeiten angepasst werden müssen
                            if (this.sA_Config && !this.sA_Config.serviceIDGenerieren) {
                                this.updateAdditionalWork(termin);
                            }
                            termin.sA_Memo_Add = terminDetails.sa_Memo_Add;
                            termin.modified = true;
                            this.dbTermine![index] = termin;
                            this.saveDBTermineInIndexedDB();
                            this.svcMessageBox.showError('Der Einsatz konnte nicht mutiert werden. Meldung: ' + error, '!! FEHLER !!');
                        },
                    });
                } else {
                    termin.notes = terminDetails.note;
                    termin.additionalWork = terminDetails.additionalWork;
                    // wenn serviceIDGenerieren nicht true ist, dann könnten noch andere Einsätze mit der gleichen Service-ID vorhanden sein,
                    // wo die zusätzliche Arbeiten angepasst werden müssen
                    if (this.sA_Config && !this.sA_Config.serviceIDGenerieren) {
                        this.updateAdditionalWork(termin);
                    }
                    termin.sA_Memo_Add = terminDetails.sa_Memo_Add;
                    termin.modified = true;
                    this.dbTermine[index] = termin;
                    this.saveDBTermineInIndexedDB();
                }
            } else {
                this.svcMessageBox.showError('Der Einsatz konnte nicht mutiert werden. Der Termin wurde nicht gefunden. ', '!! FEHLER !!');
            }
        }
    }

    // Bei anderen Einsätzen mit der gleichen Service-Id die zusätzlichen Arbeiten nachführen
    updateAdditionalWork(sourceTermin: ServiceAppModels.SA_Objekt) {
        if (this.dbTermine) {
            const otherTermine = this.dbTermine.filter(x => x.serviceID == sourceTermin.serviceID && x.rowID !== sourceTermin.rowID);
            if (otherTermine) {
                otherTermine.forEach(item => {
                    const index = this.dbTermine!.findIndex((o) => o.rowID === item.rowID);
                    this.dbTermine![index].additionalWork = sourceTermin.additionalWork;
                });
            }
        }

    }

    loadRapportLogoFromIndexedDB(): Promise<Blob> {
        return new Promise((resolve) => {
            this.localStorageService.get('rapport_header_logo', this.dbStorageSA).then((value) => {
                if (value !== false) {
                    resolve(value as Blob);
                } else {
                    throw new Error('In der IndexedDB ist kein Rapport-Header-Logo vorhanden!');
                }
            });
        });
    }

    async loadRapportLogoFile() {
        this.logoBlob = await this.loadRapportLogoFromIndexedDB();
    }

    loadRapportLogoFromBlob(): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(String(reader.result));
            };
            reader.onerror = (err) => {
                reject();
            };
            reader.readAsDataURL(this.logoBlob!);

        });
    }

    async loadRapportLogoString() {
        this.logoBase64Str = await this.loadRapportLogoFromBlob();
    }

    async loadRapportLogo(): Promise<String> {
        if (!this.rapportLogoLoaded) {
            await this.loadRapportLogoFile();
            await this.loadRapportLogoString();
            this.rapportLogoLoaded = true;
        }

        return this.logoBase64Str;
    }

    async saveRapport(serviceID: string, objectID: string, fileName: string, binaryPDF: Blob): Promise<boolean> {
        const saRappFile: ServiceAppModels.SA_RapportFile = {
            documentID: uuidv4(),
            description: 'Lieferschein',
            createdBy: this.userAuthService.getCurrentAuthenticatedUser()!.firstName + ' ' + this.userAuthService.getCurrentAuthenticatedUser()!.lastName,
            createdDate: new Date(),
            serviceID,
            objectID,
            fileName,
            binary: binaryPDF,
            uploaded: false,
        };

        this.dbRapporte = await this.localStorageService.get('rapporte', this.dbStorageSA, false, []);
        return new Promise(async (resolve) => {
            try {
                this.dbRapporte.push(saRappFile);
                await this.localStorageService.add('rapporte', this.dbRapporte, this.dbStorageSA);
                this.uploadRapport(this.blobToFile(binaryPDF, fileName), objectID, saRappFile.documentID, fileName).subscribe({
                    next: () => {
                        this.dbRapporte.find((x) => x.documentID === saRappFile.documentID)!.uploaded = true;
                        this.localStorageService.add('rapporte', this.dbRapporte, this.dbStorageSA);
                        resolve(true);
                    }, error: () => {
                        if (this.dbRapporte.find((x) => x.documentID === saRappFile.documentID)) {
                            return resolve(true);
                        }
                        return resolve(false);
                    }, complete: () => {
                        resolve(true);
                    }
                });
            } catch {
                resolve(false);
            }
        });
    }

    async getRapporte() {
        await this.localStorageService
            .get('rapporte', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Rapporte in der DB vorhanden
                    this.dbRapporte = [];
                } // Es sind schon Rapporte in der DB vorhanden
                else {
                    this.dbRapporte = (value as ServiceAppModels.SA_RapportFile[]);
                }
            });
    }

    getRapporteByServiceID(serviceID: string) {
        if (this.dbRapporte) {
            return this.dbRapporte.filter((x) => x.serviceID === serviceID);
        } else {
            return [];
        }
    }

    getRapporteByDocumentID(documentID: string): ServiceAppModels.SA_RapportFile {
        return this.dbRapporte.find((x) => x.documentID === documentID)!;
    }

    public uploadRapport(file: File, objectID: string, documentID: string, filename: string): Observable<Response> {
        if (!this.webServiceConfig) {
            throw Error('no config');
        }
        const url = this.webServiceConfig.saUrl + '/UploadRapport';

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', filename);
        formData.append('objectID', objectID);
        formData.append('documentID', documentID);

        return this.http.post<Response>(url, formData);
    }

    sendRapportMail(documentID: string, mailTemplate: ServiceAppModels.MailTemplate) {
        const formData = new FormData();

        const headers = new HttpHeaders({
            // 'Content-Type':  'multipart/form-data', --> Darf nicht vorhanden sein!!
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const params: HttpParams = new HttpParams()
            .set('documentID', documentID)
            .set('empfaenger', mailTemplate.empfaenger)
            .set('betreff', mailTemplate.betreff)
            .set('nachricht', mailTemplate.nachricht);

        const options = {
            headers, params,
        };

        const url = this.webServiceConfig!.saUrl;
        return this.http.post<Response>(url + '/SendRapport', formData, options);
    }

    async addImageToServiceAppLocalStorage(serviceID: string, objectID: string, fileName: string, binaryPic: Blob): Promise<boolean> {
        let description = fileName;
        if (this.sA_Objects) {
            const auftrag = this.sA_Objects.filter((x) => x.serviceID === serviceID)[0];
            if (auftrag) {
                description = auftrag.objectNumber.trim() + '_' + auftrag.objDescription;
            }
        }

        const saBildFile: ServiceAppModels.SA_BildFile = {
            documentID: uuidv4(),
            description,
            createdBy: this.userAuthService.getCurrentAuthenticatedUser()!.firstName + ' ' + this.userAuthService.getCurrentAuthenticatedUser()!.lastName,
            createdDate: new Date(),
            serviceID,
            objectID,
            fileName,
            binary: binaryPic,
            uploaded: false,
        };

        if (binaryPic.size === 0) {
            return false;
        }

        const value = await this.localStorageService.get('bilder', this.dbStorageSA, false, []);
        this.dbBilder = (value as ServiceAppModels.SA_BildFile[]);

        this.dbBilder.push(saBildFile);
        await this.localStorageService.add('bilder', this.dbBilder, this.dbStorageSA);
        return true;
    }

    async deleteImage(documentID: string, _objectID?: string): Promise<boolean> {
        const value = await this.localStorageService.get('bilder', this.dbStorageSA, false, []);
        this.dbBilder = (value as ServiceAppModels.SA_BildFile[]);
        const objectID = _objectID || this.dbBilder.find((b) => b.documentID === documentID)?.objectID;
        if (objectID) {
            this.dbBilder = this.dbBilder.filter((b) => b.documentID !== documentID);
            await this.localStorageService.add('bilder', this.dbBilder, this.dbStorageSA);
            if (this.svcSwissSettings.getWebConfig().debugMode) {
                console.log('Bild in IndexedDB gelöscht', objectID, documentID);
            }
        }

        return new Promise(resolve => {
            this.deleteImageWebservice(objectID || '', documentID).subscribe({
                next: () => {
                    this.dbDokumentZuordnungen = this.dbDokumentZuordnungen?.filter(doc => doc.documentID !== documentID);
                    resolve(true);
                }, error: () => {
                    resolve(false);
                }
            });
        });


    }

    getBilderByServiceID(serviceID: string) {
        if (this.dbBilder) {
            return this.dbBilder.filter((x) => x.serviceID === serviceID);
        } else {
            return [];
        }
    }

    getBildByDocumentID(documentID: string): ServiceAppModels.SA_BildFile {
        return this.dbBilder.filter((x) => x.documentID === documentID)[0];
    }

    public uploadBild(file: File, objectID: string, documentID: string, filename: string, description: string): Observable<Response> {
        if (!this.webServiceConfig) {
            throw Error('no config');
        }
        const url = this.webServiceConfig.saUrl + '/UploadBild';

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', filename);
        formData.append('objectID', objectID);
        formData.append('documentID', documentID);
        formData.append('description', description);

        return this.http.post<Response>(url, formData);
    }

    async getBilder() {
        await this.localStorageService
            .get('bilder', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Bilder in der DB vorhanden
                    this.dbBilder = [];
                } // Es sind schon Bilder in der DB vorhanden
                else {
                    this.dbBilder = (value as ServiceAppModels.SA_BildFile[]);
                }
            });
    }

    loadBilderFromIndexedDB(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorageService.get('bilder', this.dbStorageSA).then((value) => {
                if (value === false) {
                    // Es sind keine Bilder in der DB vorhanden
                    this.dbBilder = [];
                    resolve(true);
                } // Es sind schon Bilder in der DB vorhanden
                else {
                    this.dbBilder = (value as ServiceAppModels.SA_BildFile[]);
                    resolve(true);
                }
            });
        });
    }

    public deleteImageWebservice(objectID: string, documentID: string) {
        const formData = new FormData();

        const headers = new HttpHeaders({
            // 'Content-Type':  'multipart/form-data', --> Darf nicht vorhanden sein!!
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const params: HttpParams = new HttpParams()
            .set('objectID', objectID)
            .set('documentID', documentID);

        const options = {
            headers, params,
        };

        const url = this.webServiceConfig!.saUrl;
        return this.http.post<Response>(url + '/DeleteBild', formData, options);
    }

    /* Speichert in der indexed-DB eine neue Checkliste für den ausgewählten Auftrag */
    async saveCheckliste(serviceID: string, objectID: string): Promise<ServiceAppModels.SA_ChecklistUploadElement | undefined> {
        const mandant = this.userAuthService.getCurrentMandant();
        if (!mandant) {
            throw Error('Mandat not set');
        }

        const user = this.userAuthService.getCurrentAuthenticatedUser();
        const userName = user?.lastName + ' ' + user?.firstName;
        const checkListe = await this.getChecklisteTemplate(mandant);
        if (checkListe) {
            const saCheckistFile: ServiceAppModels.SA_ChecklistFile = {
                documentID: uuidv4(),
                description: 'Checkliste',
                createdBy: userName,
                createdDate: new Date(),
                serviceID,
                objectID,
                fileName: '',
                binary: checkListe.pdfFileTemplate,
                uploaded: false,
            };

            this.dbChecklisten = await this.localStorageService.get('checklisten', this.dbStorageSA, false, []);
            /* Checkliste-Namen pro Objekt eindeutig machen, damit mehrere Checklisten gespeichert werden können */
            saCheckistFile.fileName = this.getUniqueChecklisteFileName(objectID);

            this.dbChecklisten.push(saCheckistFile);
            await this.localStorageService.add('checklisten', this.dbChecklisten, this.dbStorageSA);

            this.checklistUploadElement = {
                documentID: saCheckistFile.documentID,
                objectID,
                binary: checkListe.pdfFileTemplate,
                fileName: saCheckistFile.fileName,
            };
            return this.checklistUploadElement;
        }
        return undefined;
    }

    async doUploadCheckliste(checkList: SA_ChecklistUploadElement): Promise<any> {
        return new Promise((resolve) => {
            this.uploadCheckliste(this.blobToFile(checkList.binary, checkList.fileName), checkList.objectID, checkList.documentID, checkList.fileName).subscribe({
                next: () => {
                    this.localStorageService.get('checklisten', this.dbStorageSA, false, []).then(checkLists => {
                        const index = checkLists.findIndex((x) => x.documentID === checkList.documentID);
                        if (index > -1) {
                            checkLists[index].uploaded = true;
                        }
                        this.dbChecklisten = checkLists;
                        this.localStorageService.add('checklisten', this.dbChecklisten, this.dbStorageSA).then(() => resolve(true));
                    });
                    /*
                    const checkLists = await this.localStorageService.get('checklisten', this.dbStorageSA, false, []);
                    const index = checkLists.findIndex((x) => x.documentID === checkList.documentID);
                    if (index > -1) {
                        checkLists[index].uploaded = true;
                    }
                    this.dbChecklisten = checkLists;
                    try {
                        await this.localStorageService.add('checklisten', this.dbChecklisten, this.dbStorageSA);
                    } catch (err: any) {
                        // there is an unknown error in Safari when we reupdate the checklists to set uploaded here (its done previously without any issue but havent found out why and how)
                        // not updating the updated field also does not solve the issue here
                        // we assume it might be about using lastModifiedDate but it doesnt seem to be the issue (it still fails without it being set in the file)
                        // but quick testing didnt make it work when just exchanging lastModifiedDate with lastModified (also tried with an empty indexDB
                        // see todo https://swissjira.atlassian.net/browse/EN-4215
                        // TLDR: No clue we just catch and move on
                        // after we synced it seemed to be working again without getting this issue too so maybe a breaking change during ios update?
                        // wondering if this issue might be due to updating the checklists from within an async callback of an observable and how the browser handles that 	(ಥ﹏ಥ)
                        if (!this.platform.SAFARI || !err.message.includes('Error preparing Blob/File data to be stored in object store')) {
                            throw err;
                        }
                        resolve(false);
                    }
                    resolve(true);

                     */
                }, error: (err) => {
                    console.log(err);
                    resolve(false);
                }
            });
        });
    }

    public uploadCheckliste(file: File, objectID: string, documentID: string, filename: string): Observable<Response> {
        if (!this.webServiceConfig) {
            throw Error('no config');
        }
        const url = this.webServiceConfig.saUrl + '/UploadCheckliste';

        const formData = new FormData();
        formData.append('file', file);
        formData.append('objectID', objectID);
        formData.append('documentID', documentID);
        formData.append('fileName', filename);

        formData.append('file', file); // Der 1. Parameter 'file' muss übereinstimmen mit dem Parameter im Endpunkt
        return this.http.post<Response>(url, formData);
    }

    async delDokument(documentID: string): Promise<boolean> {
        return new Promise(async resolve => {
            let dokuSource = 0;
            let objectID = '';

            // Dokumente können an verschiedenen Orten gespeichert sein: Rapporte (Lieferschein), Checklisten oder andere Dokumente aus dem Navi
            // Der dokuSource ermittelt, woher das Dokument kommt. 1 = Rapporte (Lieferschein), 2 = Checkliste oder 3 = andere Dokumente aus dem Navi

            // Ist das zu löschende Dokument als Rapport vorhanden?
            if (this.dbRapporte && this.dbRapporte.length > 0) {
                const docu = this.dbRapporte?.filter((d) => d.documentID === documentID);
                if (docu && docu.length === 1) {
                    dokuSource = 1;
                    objectID = docu[0].objectID;
                }
            }

            // Ist das zu löschende Dokument als Checkliste vorhanden?
            if (dokuSource === 0 && this.dbChecklisten && this.dbChecklisten.length > 0) {
                const docu = this.dbChecklisten?.filter((d) => d.documentID === documentID);
                if (docu && docu.length === 1) {
                    dokuSource = 2;
                    objectID = docu[0].objectID;
                }
            }

            // Ist das zu löschende Dokument als normales Navi-Dokument vorhanden?
            if (dokuSource === 0 && this.dbDokumente && this.dbDokumente.length > 0) {
                const docu = this.dbDokumente?.filter((d) => d.documentID === documentID);
                if (docu && docu.length === 1) {
                    dokuSource = 3;
                    objectID = docu[0].objectID;
                }
            }

            if (dokuSource > 0) {
                switch (dokuSource) {
                    case 1:
                        this.dbRapporte = this.dbRapporte.filter((r) => r.documentID !== documentID);
                        await this.localStorageService.add('rapporte', this.dbRapporte, this.dbStorageSA);
                        break;
                    case 2:
                        this.dbChecklisten = this.dbChecklisten.filter((chk) => chk.documentID !== documentID);
                        await this.localStorageService.add('checklisten', this.dbChecklisten, this.dbStorageSA);
                        break;
                    case 3:
                        if (this.dbDokumente) {
                            this.dbDokumente = this.dbDokumente.filter((d) => d.documentID !== documentID);
                            await this.localStorageService.add('objektDokumente', this.dbDokumente, this.dbStorageSA);
                        }
                        break;
                }

                this.deleteDoku(objectID, documentID).subscribe({
                    next: () => {
                        resolve(true);
                    }, error: () => {
                        resolve(false);
                    }
                });
            }
        });
    }

    deleteDoku(objectID: string, documentID: string) {
        const formData = new FormData();

        const headers = new HttpHeaders({
            // 'Content-Type':  'multipart/form-data', --> Darf nicht vorhanden sein!!
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const params: HttpParams = new HttpParams()
            .set('objectID', objectID)
            .set('documentID', documentID);

        const options = {
            headers, params,
        };

        const url = this.webServiceConfig!.saUrl;
        return this.http.post<Response>(url + '/DeleteDoku', formData, options);
    }

    updateAppointment(appointment: ServiceAppModels.Appointment): Promise<boolean> {
        if (this.svcSwissSettings.getWebConfig().debugMode) {
            console.log('Termin-After', appointment);
        }

        return new Promise((resolve) => {
            this.doUpdateAppointment(appointment).subscribe({
                next: () => resolve(true), error: () => resolve(false),
            });
        });
    }

    async updateAppointmentInLocalDB(appointment: ServiceAppModels.Appointment) {
        if (this.dbTermine) {
            const index = this.dbTermine.findIndex((x) => x.rowID === appointment.Id);
            const termin = this.dbTermine[index];
            termin.startTime = this._datumFormatPipe.transform(appointment.StartTime, 'yyyy-MM-ddTHH:mm:ss');
            termin.endTime = this._datumFormatPipe.transform(appointment.EndTime, 'yyyy-MM-ddTHH:mm:ss');
            this.dbTermine[index] = termin;
            await this.saveDBTermineInIndexedDB();
        }
    }

    /* ServiceApp Agenda */

    addEmpWorkPlanEntry(assignment: ServiceAppModels.Assignment) {
        const url = this.webServiceConfig!.saUrl;
        return this.http.post<ServiceAppModels.Assignment>(url + '/AddEmpWorkPlan', assignment, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    updateEmpWorkPlanEntry(assignment: ServiceAppModels.Assignment) {
        const url = this.webServiceConfig!.saUrl;
        return this.http.post<ServiceAppModels.Assignment>(url + '/UpdEmpWorkPlan', assignment, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    delEmpWorkPlanEntry(assignment: ServiceAppModels.Assignment) {
        const url = this.webServiceConfig!.saUrl;
        return this.http.post<ServiceAppModels.Assignment>(url + '/DelEmpWorkPlan', assignment, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    reverseGeocoding(lat: number, lng: number) {
        return this.http.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
    }

    public async saveTimeRecordingItem(timeRecording: ServiceAppModels.TimeRecordingItem) {
        this.checkStartTime(timeRecording).then(async (x) => {
            timeRecording = x;
            this.timeRecordingItems = [];
            this.timeRecordingItems.push(timeRecording);
            await this.localStorageService.add('timerecording', this.timeRecordingItems, this.dbStorageSA);
        });

    }

    public getTimeRecordingItems(): Promise<ServiceAppModels.TimeRecordingItem[] | undefined> {
        return this.localStorageService
            .get('timerecording', this.dbStorageSA)
            .then((value) => {
                if (value === false) {
                    // Es konnte keinen Wert in der indexeddb für timeControlEntries ermittelt werden
                    return undefined;
                } else {
                    this.timeRecordingItems = (value as ServiceAppModels.TimeRecordingItem[]);
                    return this.timeRecordingItems;
                }
            });
    }

    public addTimeRecording(timeRecording: ServiceAppModels.TimeRecordingItem): boolean {
        if (timeRecording && timeRecording.endTime) {
            const desc = this.dbKostenstellen!.filter((k) => k.costCenterID == timeRecording.costCenterID)[0].description;
            const duration = this._datumFormatPipe.ouersBetweenToDates(timeRecording.startTime!, timeRecording.endTime);

            // Time_Entry Objekt erstellen und hinzufügen
            const timeEntry: ServiceAppModels.SA_TimeEntry = {
                objectID: timeRecording.objectID,
                servicePosID: uuidv4(),
                employeeIDS: [],
                serviceID: timeRecording.serviceID,
                costCenterID: timeRecording.costCenterID,
                activityDescription: desc,
                startDate: timeRecording.startTime,
                endDate: timeRecording.endTime,
                duration,
                deleted: false,
                uploadedToServicePos: false,
                uploadedToTimeControlEntries: false,
            };
            if (timeRecording.workPlace && timeRecording.workPlace != '') {
                timeEntry.workPlace = timeRecording.workPlace;
            }

            timeEntry.employeeIDS.push(timeRecording.employeeID);
            this.dbAuftragsZeiten?.push(timeEntry);

            this.localStorageService
                .add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA)
                .then(async () => {
                    // Bestehendes Item überschreiben, da nur eine Pause gemacht wird.
                    this.timeRecordingItems[this.timeRecordingItems.length - 1] = timeRecording;
                    await this.localStorageService.add('timerecording', this.timeRecordingItems, this.dbStorageSA);
                });
            return true;
        } else {
            return false;
        }
    }

    public stopTimeRecording(timeRecording: ServiceAppModels.TimeRecordingItem, alreadySaved: boolean): boolean {
        if (timeRecording && timeRecording.endTime) {
            if (!alreadySaved) {
                const desc = this.dbKostenstellen!.filter((k) => k.costCenterID == timeRecording.costCenterID)[0].description;
                const duration = this._datumFormatPipe.ouersBetweenToDates(timeRecording.startTime!, timeRecording.endTime);

                // Time_Entry Objekt erstellen und hinzufügen
                const timeEntry: ServiceAppModels.SA_TimeEntry = {
                    objectID: timeRecording.objectID,
                    servicePosID: uuidv4(),
                    employeeIDS: [],
                    serviceID: timeRecording.serviceID,
                    costCenterID: timeRecording.costCenterID,
                    activityDescription: desc,
                    startDate: timeRecording.startTime,
                    endDate: timeRecording.endTime,
                    duration,
                    deleted: false,
                    uploadedToServicePos: false,
                    uploadedToTimeControlEntries: false,
                };
                if (timeRecording.workPlace && timeRecording.workPlace != '') {
                    timeEntry.workPlace = timeRecording.workPlace;
                }
                timeEntry.employeeIDS.push(timeRecording.employeeID);
                this.dbAuftragsZeiten?.push(timeEntry);
                this.localStorageService
                    .add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA)
                    .then(() => {
                        this.localStorageService.delete('timerecording', this.dbStorageSA);
                    });
            } else {
                this.localStorageService.delete('timerecording', this.dbStorageSA);
            }
            return true;
        } else {
            return false;
        }
    }

    public getAuftragsTotalGeplant(serviceID: string): number {
        let auftragsTotal = 0.0;
        if (this.dbAuftragsZeiten) {
            const auftragsZeiten = this.dbAuftragsZeiten.filter((x) => x.serviceID === serviceID);
            if (auftragsZeiten && auftragsZeiten.length > 0) {
                auftragsZeiten.forEach((az) => {
                    auftragsTotal = auftragsTotal + az.duration;
                });
            }
        }
        return Math.round(auftragsTotal * 100) / 100;
    }

    public getAuftragsTotalUngeplant(objectID: string): number {
        let auftragsTotal = 0.0;
        if (this.dbSA_TimeControlEntries) {
            const auftragsZeitenUngeplant = this.dbSA_TimeControlEntries.filter((x) => x.fk_ObjectID === objectID);
            if (auftragsZeitenUngeplant && auftragsZeitenUngeplant.length > 0) {
                auftragsZeitenUngeplant.forEach((az) => {
                    auftragsTotal = auftragsTotal + az.originalTimeAmount;
                });
            }
        }
        return Math.round(auftragsTotal * 100) / 100;
    }

    public getTagesTotal(): number {
        let tagesTotal = 0.0;
        const heute = this._datumFormatPipe.removeTimeFromDate(new Date());
        const morgen = new Date(heute);
        morgen.setDate(morgen.getDate() + 1);
        if (this.dbAuftragsZeiten) {
            const auftragsZeiten = this.dbAuftragsZeiten.filter((x) => x.startDate !== undefined && heute <= x.startDate && x.startDate < morgen);
            if (auftragsZeiten && auftragsZeiten.length > 0) {
                auftragsZeiten.forEach((az) => {
                    tagesTotal = tagesTotal + Number(az.duration);
                });
            }
        }

        // Auf 2 Stellen nach dem Komma runden
        return Math.round(tagesTotal * 100) / 100;
    }

    public getSuchfeldBeschreibungen(): Promise<ServiceAppModels.SearchFieldResponseItem[]> {
        return new Promise((resolve) => {
            if (this.dbTermine) {
                const searchFieldObjs: ServiceAppModels.SearchFieldResponseItem[] = [];
                this.dbTermine.forEach((saObj) => {
                    const item: ServiceAppModels.SearchFieldResponseItem = {
                        objectID: saObj.objectID, serviceID: saObj.serviceID, beschreibung: saObj.objectNumber.trim() + ' ' + saObj.objDescription,
                    };

                    if (item.serviceID) {
                        item.beschreibung = item.beschreibung + ' ' + this._datumFormatPipe.transformOnlyDate(saObj.startTime);
                    }
                    searchFieldObjs.push(item);
                });
                resolve(searchFieldObjs);
            } else {
                resolve([]);
            }
        });
    }

    getLastChosenView(): string {
        return this.lastSchedulerView;
    }

    setLastChosenView(view: string) {
        this.lastSchedulerView = view;
    }

    // SYNC Kommunikation - START
    showSyncMessage(message: string) {
        if (this.reloadSASyncStateFunc) {
            const zeitpunkt = this._datumFormatPipe.transform(new Date());
            this.reloadSASyncStateFunc(zeitpunkt + ' ' + message);
        }
    }

    showSyncProgressState(value: number) {
        if (this.reloadSASyncStateProgressBarFunc) {
            this.reloadSASyncStateProgressBarFunc(value);
        }
    }

    showSyncDocumentsState(anzahl: number, total: number) {
        if (this.reloadSASyncStateDocuFunc) {
            this.reloadSASyncStateDocuFunc(anzahl, total);
        }
    }

    cancelSync() {
        this.doCancelSync = true;
    }

    getCurrentSAConfig(): Observable<ServiceAppModels.SA_Config> {
        const url = this.webServiceConfig!.saUrl;
        return this.http.get<ServiceAppModels.SA_Config>(url + '/Einstellungen?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()!.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getDefaultObjekt(): Promise<ServiceAppModels.SA_DefaultObjekt> {
        return new Promise<ServiceAppModels.SA_DefaultObjekt>((resolve, reject) => {
            const url = this.webServiceConfig!.saUrl;
            this.http.get<ServiceAppModels.SA_DefaultObjekt>(url + '/StandardObjekt?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()!.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).subscribe({
                next: (x) => {
                    resolve(x);
                }, error: (err) => {
                    reject();
                }
            });
        });

    }

    // SYNC UP: START UPLOAD TO WEBSERVICE
    async startUploadData(): Promise<boolean> {
        this.doCancelSync = false;
        let hasError = false;
        this.showSyncMessage('Lade die Objekte aus der lokalen DB.');
        this.showSyncProgressState(1);
        if (this.doCancelSync) {
            return false;
        }
        const res = await this.loadObjekteFromIndexedDB(false);
        if (!res || this.doCancelSync) {
            return false;
        }
        this.showSyncMessage('Lokale Objekte erfolgreich geladen.');
        this.showSyncProgressState(3);
        if (this.svcSwissSettings.getWebConfig().debugMode) {
            console.log('Objekte/Aufträge/Termine geladen...');
        }
        this.showSyncMessage('Lade die erfassten Zeiten aus der lokalen DB.');

        const res2 = await this.loadTimeEntriesFromIndexedDB();
        if (!res2 || this.doCancelSync) {
            return false;
        }
        this.showSyncMessage('Lokale Zeiten erfolgreich geladen.');
        this.showSyncProgressState(5);

        // Bestehende Einträge aus der Indexed-DB für die Service-Pos laden
        this.showSyncMessage('Lade die erfassten Positionen aus der lokalen DB.');
        const res3 = await this.loadServicePosFromIndexedDB();
        if (!res3 || this.doCancelSync) {
            return false;
        }
        this.showSyncMessage('Lokale Positionen erfolgreich geladen.');
        this.showSyncProgressState(7);

        const res4 = await this.loadTimeControlEntriesFromIndexedDB();
        if (!res4 || this.doCancelSync) {
            return false;
        }
        this.showSyncMessage('Ermittle Zeiten für Upload.');
        // Erfasste Zeitaufwände hochladen
        const res5 = await this.uploadTimeJournal();
        if (!res5 || this.doCancelSync) {
            return false;
        }
        this.showSyncMessage('Arbeitszeiten übermittelt.');
        this.showSyncProgressState(9);

        const res6 = await this.loadProductEntriesFromIndexedDB();
        this.showSyncMessage('Produkte ermittelt für Upload.');
        this.showSyncProgressState(11);
        if (!res6 || this.doCancelSync) {
            return false;
        }

        const res7 = await this.uploadServices();
        if (!res7 || this.doCancelSync) {
            return false;
        }

        this.showSyncMessage('Produkte erfolgreich übermittelt.');
        this.showSyncProgressState(13);

        // Erfasste Rapporte hochladen, wenn diese noch nicht übermittelt wurden
        const res8 = await this.uploadRapporte();
        if (!res8.success) {
            res8.rejected.forEach(s => {
                this.showSyncMessage('Rapport ' + s + ' konnte nicht übermittelt werden');
            });

            this.showSyncMessage(res8.rejected.length + ' Rapporte wurden nicht übermittelt');
            this.showSyncMessage(res8.fulfilled.length + ' Rapporte wurden erfolgreich übermittelt');
            hasError = true;
        } else {
            this.showSyncMessage(res8.fulfilled.length + ' Rapporte wurden erfolgreich übermittelt.');
        }
        if (this.doCancelSync) {
            return false;
        }
        this.showSyncProgressState(14);

        // Erfasste Bilder hochladen, wenn diese noch nicht übermittelt wurden
        const res9 = await this.uploadBilder();
        if (!res9.success) {
            res9.rejected.forEach(s => {
                this.showSyncMessage('Bild ' + s + ' konnte nicht übermittelt werden');
            });

            this.showSyncMessage(res9.rejected.length + ' Bilder wurden nicht übermittelt');
            this.showSyncMessage(res9.fulfilled.length + ' Bilder wurden erfolgreich übermittelt');
            hasError = true;
        } else {
            this.showSyncMessage('Bilder erfolgreich übermittelt.');
        }
        this.showSyncProgressState(16);
        if (this.doCancelSync) {
            return false;
        }
        const res10 = await this.uploadChecklisten();
        if (!res10.success) {
            res10.rejected.forEach(s => {
                this.showSyncMessage('CheckListe ' + s + ' konnte nicht übermittelt werden');
            });

            this.showSyncMessage(res10.rejected.length + ' CheckListe wurden nicht übermittelt');
            this.showSyncMessage(res10.fulfilled.length + ' CheckListe wurden erfolgreich übermittelt');
            hasError = true;
        } else {
            this.showSyncMessage('Checklisten erfolgreich übermittelt.');
        }
        this.showSyncProgressState(18);
        if (this.doCancelSync) {
            return false;
        }
        const res11 = await this.uploadDokumente();
        if (!res11.success) {
            res11.rejected.forEach(s => {
                this.showSyncMessage('Dokument ' + s + ' konnte nicht übermittelt werden');
            });

            this.showSyncMessage(res11.rejected.length + ' Dokument wurden nicht übermittelt');
            this.showSyncMessage(res11.fulfilled.length + ' Dokument wurden erfolgreich übermittelt');
            hasError = true;
        } else {
            this.showSyncMessage('Alle Dokumente erfolgreich übermittelt.');
        }
        this.showSyncProgressState(20);
        if (this.doCancelSync) {
            return false;
        }
        const res12 = await this.uploadObjectDocumentsWithChanges();
        if (!res12.success) {
            res12.rejected.forEach(s => {
                this.showSyncMessage('Dokumentänderungen ' + s + ' konnte nicht übermittelt werden');
            });

            this.showSyncMessage(res12.rejected.length + ' Dokumentänderungen wurden nicht übermittelt');
            this.showSyncMessage(res12.fulfilled.length + ' Dokumentänderungen wurden erfolgreich übermittelt');
            hasError = true;
        } else {
            this.showSyncMessage('Alle Dokumentänderungen erfolgreich übermittelt.');
        }
        this.showSyncProgressState(21);

        const res13 = await this.uploadModifiedObjects();
        if (!res13.success) {
            res13.rejected.forEach(s => {
                this.showSyncMessage('Objekt ' + s + ' konnte nicht übermittelt werden');
            });

            this.showSyncMessage(res13.rejected.length + ' Objekt wurden nicht übermittelt');
            this.showSyncMessage(res13.fulfilled.length + ' Objekt wurden erfolgreich übermittelt');
            hasError = true;
        } else {
            this.showSyncMessage('Alle Objektänderungen erfolgreich übermittelt.');
        }

        this.dbTermine = this.dbTermine?.filter((x) => x.serviceComplete === 0);
        if (this.reloadAppointmentsFunc) {
            this.reloadAppointmentsFunc();
        }
        if (this.reloadOrdersFunc) {
            this.reloadOrdersFunc();
        }

        this.showSyncProgressState(22);
        return !hasError;
    }

    public isDayClosed(day: Date): boolean {
        const datum = this._datumFormatPipe.transformOnlyDate(day);
        if (this.dbSA_TimeControlDays && this.dbSA_TimeControlDays.length > 0) {
            return (this.dbSA_TimeControlDays.filter((y) => y.datum === datum && y.closed).length > 0);
        } else {
            return false;
        }
    }

    async saveTimeEntryToTimeControlEntries(timeEntry: ServiceAppModels.SA_TimeEntry) {
        try {
            let timeObj: any = undefined;
            if (timeEntry.serviceID) {
                timeObj = this.sA_Objects?.filter((x) => x.serviceID === timeEntry.serviceID)[0];
            } else {
                timeObj = this.sA_Objects?.filter((x) => x.objectID === timeEntry.objectID && x.serviceID === null)[0];
            }

            if (!timeObj) {
                const defaultObj: any = await this.getDefaultObjekt();
                defaultObj.startTime = new Date();
                timeObj = defaultObj;
            }

            if (timeObj) {
                // due to legacy stuff it could be a string in some cases apparently
                let entryDate;
                if (!timeEntry.startDate) {
                    throw Error('EntryDate is missing or can not be set');
                }

                if (timeEntry.startDate instanceof Date) {
                    entryDate = timeEntry.startDate;
                } else {
                    entryDate = new Date(timeEntry.startDate);
                }

                if (!entryDate) {
                    throw Error('EntryDate is missing or can not be set');
                }

                // Stunden und Minuten entfernen
                if (entryDate.toString().length > 10) {
                    entryDate = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
                    const offSet = -1 * entryDate.getTimezoneOffset();
                    entryDate.setMinutes(offSet); // Korrektur der Minuten wegen der Zeitzone
                }
                entryDate = this._datumFormatPipe.transformOnlyDateEN(entryDate);

                timeEntry.employeeIDS.forEach((employeeID) => {
                    // Prüfen, ob die erfasste Zeit(en) schon in den TimeControlEntries gespeichert wurden
                    const Index = this.dbSA_TimeControlEntries.findIndex((x) => x.servicePosID === timeEntry.servicePosID && x.fk_EmployeeID === employeeID);
                    let myRowID;
                    if (Index >= 0) {
                        myRowID = this.dbSA_TimeControlEntries[Index].rowID;
                    } else {
                        myRowID = uuidv4();
                    }

                    // Eintrag erstellen
                    const timeControlEntry: ServiceAppModels.SA_TimeControlEntry = {
                        rowID: myRowID,
                        mandantID: this.userAuthService.getCurrentAuthenticatedUser()!.mandatorID,
                        servicePosID: timeEntry.servicePosID,
                        fk_ObjectID: timeObj!.objectID,
                        fk_EmployeeID: employeeID,
                        fk_ServiceID: timeEntry.serviceID,
                        fk_CostCenter: timeEntry.costCenterID,
                        description: timeEntry.activityDescription,
                        expenseID: timeEntry.expenseID!,
                        entryDate,
                        originalStartTime: timeEntry.endDate ? timeEntry.startDate : undefined,
                        originalEndTime: timeEntry.endDate,
                        originalTimeAmount: timeEntry.duration,
                        objectNumber: timeObj!.objectNumber,
                        transfered: 0,
                        expenseAmount: timeEntry.expenseAmount,
                        expenseRate: timeEntry.expensePrice,
                        note: timeEntry.note,
                        workplace: timeEntry.workPlace,
                        deleted: timeEntry.deleted,
                        uploaded: false,
                    };

                    if (Index === -1) {
                        this.dbSA_TimeControlEntries.push(timeControlEntry);
                    } else {
                        this.dbSA_TimeControlEntries[Index] = timeControlEntry;
                    }
                });

                // Bestehende Einträge auf gelöscht setzen, welche für einen Mitarbeiter nun nicht mehr vorhanden sind
                const servicePosTimeControlEntries = this.dbSA_TimeControlEntries.filter((x) => x.servicePosID === timeEntry.servicePosID);
                servicePosTimeControlEntries.forEach((timeControlEntry) => {
                    if (timeEntry.employeeIDS.indexOf(timeControlEntry.fk_EmployeeID) == -1) {
                        // Der Eintrag wurde nicht gefunden, also auf gelöscht setzen
                        this.dbSA_TimeControlEntries.filter((tce) => tce.rowID === timeControlEntry.rowID)[0].deleted = true;
                    }
                });
            } else {
                this.svcMessageBox.showError('saveTimeEntryToTimeControlEntries(): Zu einer erfassten Zeit konnte kein Objekt ermittet werden. ServicePosID: ' + timeEntry.servicePosID, '!!! Fehler !!!');
            }
        } catch (ex) {
            console.error('saveTimeEntryToTimeControlEntries(): Fehler beim Speichern einer Zeit in die TimeControlEntries.', ex);
        }
    }

    uploadServices() {
        return new Promise(async (resolve) => {
            let errorExists = false;
            if (this.employee) {
                if (this.dbAuftragsProdukte) {
                    let doUpdateIndexDB = false;
                    this.dbAuftragsProdukte.forEach((sa_ProductEntry) => {
                        if (this.svcSwissSettings.getWebConfig().debugMode) {
                            console.log('UploadServices: ', sa_ProductEntry);
                        }

                        // Zeiten/Material werden nur für die Weiterverarbeitung gespeichert, wenn der Service abgeschlossen ist
                        const termin = this.dbTermine?.find((x) => x.serviceID === sa_ProductEntry.serviceID);


                        if (termin && termin.serviceComplete === 1) {
                            this.saveOrderProductToServicePos(sa_ProductEntry);
                            doUpdateIndexDB = true;
                        }
                    });
                    if (doUpdateIndexDB) {
                        await this.saveServicePosInIndexedDB();
                    }

                    this.dbSA_ServicePos = (await this.localStorageService.get<ServiceAppModels.SA_ServicePos[]>('servicePos', this.dbStorageSA, false, []));
                    if (this.dbSA_ServicePos) {
                        const servicePos = this.dbSA_ServicePos.filter((x) => !x.uploaded);
                        if (servicePos && servicePos.length > 0) {
                            this.doUploadServicePos(servicePos).then(res => {
                                const resultList: ServiceAppModels.SA_ServicePosResult[] = (res as any).servicePosResultList;
                                if (this.svcSwissSettings.getWebConfig().debugMode) {
                                    console.log('resultList ServicePos', resultList);
                                }

                                resultList.forEach((servicePosRes) => {
                                    let isExpenses = false;
                                    // Die Position konnte erfolgreich gespeichert werden
                                    if (!servicePosRes.hasError) {
                                        let index = this.dbAuftragsProdukte!.findIndex((x) => x.servicePosID === servicePosRes.servicePosID);

                                        // wenn der index -1 ist, dann handelt es sich um Spesen und nicht um Material
                                        if (index === -1) {
                                            isExpenses = true;
                                            index = this.dbSA_ServicePos.findIndex((x) => x.servicePosID === servicePosRes.servicePosID);
                                        }

                                        // Prüfen, ob die Zeitzeile eventuell mehrere Mitarbeiter hatte
                                        // Falls ja → dann müssen alle Mitarbeiter geprüft werden.
                                        const allePos = resultList.filter((x) => x.servicePosID === servicePosRes.servicePosID).length;
                                        const allePosOhneFehler = resultList.filter((x) => x.servicePosID === servicePosRes.servicePosID && !x.hasError).length;
                                        if (allePos === allePosOhneFehler) {
                                            // Keine Fehler vorhanden!
                                            if (index >= 0) {
                                                if (isExpenses) {
                                                    this.dbSA_ServicePos[index].uploaded = true;
                                                } else {
                                                    this.dbAuftragsProdukte![index].uploaded = true;
                                                    this.dbSA_ServicePos.filter((x) => x.servicePosID === servicePosRes.servicePosID)[0].uploaded = true;
                                                }
                                            }
                                        } else {
                                            // Hier muss eine Liste mit Fehlern erstellt werden.
                                            this.showSyncMessage('!!! Fehler !!! Alle => (uploadServices() 1): Position konnte nicht gespeichert werden. ServicePosID: ' + servicePosRes.servicePosID);
                                            errorExists = true;
                                        }
                                    } else {
                                        // Hier muss eine Liste mit Fehlern erstellt werden.
                                        this.showSyncMessage('!!! Fehler !!! Alle => (uploadServices() 2): Position konnte nicht gespeichert werden. ServicePosID: ' + servicePosRes.servicePosID);
                                        errorExists = true;
                                    }
                                });
                                // Angepasste Zeiten in IndexedDB speichern
                                this.localStorageService.add('artikel', this.dbAuftragsProdukte, this.dbStorageSA);
                                this.localStorageService.add('servicePos', this.dbSA_ServicePos, this.dbStorageSA);
                                if (errorExists) {
                                    resolve(false);
                                } else {
                                    resolve(true);
                                }
                            }).catch((error) => {
                                console.error('Fehler beim Übermitteln der Service-Positionen: ' + error);
                                resolve(false);
                            });
                        } else {
                            // Keine Daten für den Upload vorhanden
                            resolve(true);
                        }
                    } else {
                        resolve(false);
                    }
                } else {
                    resolve(false);
                }
            } else {
                resolve(false);
            }
        });
    }

    // SYNC Kommunikation - ENDE

    // SYNC GetCurrentConfig
    public saveFilterSettings(filterValue: string) {
        this.localStorageService.add('timeControlFilter', filterValue, this.dbStorageSA);
    }

    public loadFilterSettings(): Promise<string> {
        return new Promise((resolve) => {
            this.localStorageService.get('timeControlFilter', this.dbStorageSA).then((value) => {
                if (value === false) {
                    resolve('');
                } else {
                    resolve(value as string);
                }
            });
        });
    }

    async uploadRapporte(): Promise<{ success: boolean, fulfilled: string[], rejected: string[] }> {
        const rapports: SA_RapportFile[] = (await this.localStorageService.get('rapporte', this.dbStorageSA, false, []));
        const rapporteToUpload = rapports.filter((x) => !x.uploaded);
        const promises: Promise<string>[] = [];
        for (const report of rapporteToUpload) {
            promises.push(
                new Promise((resolve, reject) => {
                    if (!report.binary || !report.binary.size || report.binary.size <= 0) {
                        this.showSyncMessage(`Rapporte ${report.fileName} (${report.documentID}) kann nicht synchronisiert werden. Die Datei existiert nicht`);
                        reject(report.documentID);
                        return;
                    }
                    const blob = this.blobToFile(report.binary, report.fileName);
                    this.uploadRapport(blob, report.objectID, report.documentID, report.fileName).subscribe({
                        next: () => {
                            resolve(report.documentID);
                        }, error: (err) => {
                            reject(report.documentID);
                        },
                    });
                })
            );
        }
        const fulfilled: string[] = [];
        const rejected: string[] = [];
        const result = await Promise.allSettled(promises);
        result.forEach(res => {
            if (res.status === 'fulfilled') {
                const i = rapports.findIndex(r => r.documentID === res.value);
                if (i > -1) {
                    rapports[i].uploaded = true;
                    fulfilled.push(res.value);
                } else {
                    console.error('Fehler beim Hochladen eines Rapports: wasnt found again to set uploaded for ' + res.value);
                    rejected.push(res.value);
                }
            } else {
                console.error('Fehler beim Hochladen eines Rapports ' + res.reason);
                rejected.push(res.reason);
            }
        });
        await this.localStorageService.add('rapporte', rapports, this.dbStorageSA);
        return {success: result.length === fulfilled.length, fulfilled, rejected};
    }

    async uploadBilder(): Promise<{ success: boolean, fulfilled: string[], rejected: string[] }> {
        const images: SA_BildFile[] = await this.localStorageService.get('bilder', this.dbStorageSA, false, []);
        const imagesToUpload = images.filter((x) => !x.uploaded);
        const promises: Promise<string>[] = [];
        for (const image of imagesToUpload) {
            promises.push(
                new Promise((resolve, reject) => {
                    if (!image.binary || !image.binary.size || image.binary.size <= 0) {
                        this.showSyncMessage(`Bild ${image.fileName} (${image.documentID}) kann nicht gefunden werden. Die Datei existiert nicht`);
                        reject(image.documentID);
                        return;
                    }
                    this.uploadBild(this.blobToFile(image.binary, image.fileName), image.objectID, image.documentID, image.fileName, image.description).subscribe({
                        next: () => {
                            resolve(image.documentID);
                        }, error: (err) => {
                            reject(image.documentID);
                        },
                    });
                })
            );
        }
        const fulfilled: string[] = [];
        const rejected: string[] = [];
        const result = await Promise.allSettled(promises);
        result.forEach(res => {
            if (res.status === 'fulfilled') {
                const i = images.findIndex(r => r.documentID === res.value);
                if (i > -1) {
                    images[i].uploaded = true;
                    fulfilled.push(res.value);
                } else {
                    console.error('Fehler beim Hochladen eines Bildes: wasnt found again to set uploaded for ' + res.value);
                    rejected.push(res.value);
                }
            } else {
                console.error('Fehler beim Hochladen eines Bildes ' + res.reason);
                rejected.push(res.reason);
            }
        });
        await this.localStorageService.add('bilder', images, this.dbStorageSA);
        return {success: result.length === fulfilled.length, fulfilled, rejected};
    }

    uploadTimeJournal(): Promise<Boolean> {
        return new Promise((resolve) => {
            if (this.employee) {
                if (this.employee.sA_Dontsendtimestotimecontrol === 0) {
                    this.uploadTimeJournalToTimeControlEntries().then(() => {
                        this.uploadTimeJournalToServicePos().then(() => {
                            resolve(true);
                        });
                    });
                } else {
                    this.uploadTimeJournalToServicePos().then(() => {
                        resolve(true);
                    });
                }
            } else {
                resolve(false);
            }
        });
    }

    async uploadChecklisten(): Promise<{ success: boolean, fulfilled: string[], rejected: string[] }> {
        const checklists: SA_ChecklistFile[] = await this.localStorageService.get('checklisten', this.dbStorageSA, false, []);
        const checklistsToUpload = checklists.filter((x) => !x.uploaded);
        const promises: Promise<string>[] = [];
        for (const checklist of checklistsToUpload) {
            promises.push(
                new Promise((resolve, reject) => {
                    if (!checklist.binary || !checklist.binary.size || checklist.binary.size <= 0) {
                        this.showSyncMessage(`Checkliste ${checklist.fileName} (${checklist.documentID}) kann nicht gefunden werden. Die Datei existiert nicht`);
                        reject(checklist.documentID);
                        return;
                    }
                    this.uploadCheckliste(this.blobToFile(checklist.binary, checklist.fileName), checklist.objectID, checklist.documentID, checklist.fileName).subscribe({
                        next: () => {
                            resolve(checklist.documentID);
                        }, error: (err) => {
                            reject(checklist.documentID);
                        },
                    });
                })
            );
        }
        const fulfilled: string[] = [];
        const rejected: string[] = [];
        const result = await Promise.allSettled(promises);
        result.forEach(res => {
            if (res.status === 'fulfilled') {
                const i = checklists.findIndex(r => r.documentID === res.value);
                if (i > -1) {
                    checklists[i].uploaded = true;
                    fulfilled.push(res.value);
                } else {
                    console.error('Fehler beim Hochladen eines Checkliste: wasnt found again to set uploaded for ' + res.value);
                    rejected.push(res.value);
                }
            } else {
                console.error('Fehler beim Hochladen einer Checkliste ' + res.reason);
                rejected.push(res.reason);
            }
        });
        await this.localStorageService.add('checklisten', checklists, this.dbStorageSA);
        return {success: result.length === fulfilled.length, fulfilled, rejected};
    }

    async uploadDokumente(): Promise<{ success: boolean, fulfilled: string[], rejected: string[] }> {
        const documentsToUpload: string[] = await this.loadDokumenteFromIndexedDB();
        const promises: Promise<ServiceAppModels.DokumentDBFile>[] = [];
        for (const docID of documentsToUpload) {
            const doc: ServiceAppModels.DokumentDBFile = await this.localStorageService.get(docID, this.dbStorageSA_Doku, true);
            if (doc && !doc.uploaded) {
                promises.push(
                    new Promise((resolve, reject) => {
                        if (!doc.binary || !doc.binary.size) {
                            this.showSyncMessage(`Dokument ${doc.fileName} (${doc.documentID}) kann nicht gefunden werden`);
                            reject(doc.documentID);
                            return;
                        }
                        const dokuInfo: DokumentModels.DokumentInfo = {
                            objectID: doc.objectID,
                            documentID: docID,
                            fileName: doc.fileName,
                            description: doc.description,
                            dokumentTyp: DokumentModels.DokumentTyp.Dokument,
                            dokumentQuelle: DokumentModels.DokumentSource.SA_DBDokumente,
                            lastModified: doc.lastModified || new Date()
                        };
                        // this.dokuDataSVC.
                        const file = this.blobToFile(doc.binary, doc.fileName);
                        this.uploadDokument(dokuInfo, file).subscribe({
                            next: () => {
                                resolve(doc);
                            }, error: (err) => {
                                reject(doc);
                            }
                        });
                    })
                );
            }
        }
        const fulfilled: string[] = [];
        const rejected: string[] = [];
        const result = await Promise.allSettled(promises);
        for (const res of result) {
            if (res.status === 'fulfilled') {
                const doc = res.value;
                doc.uploaded = true;
                await this.localStorageService.add(doc.documentID, doc, this.dbStorageSA_Doku);
            } else {
                console.error('Fehler beim Hochladen des Dokumentes ' + res.reason);
                rejected.push(res.reason);
            }
        }
        return {success: result.length === fulfilled.length, fulfilled, rejected};
    }

    async uploadObjectDocumentsWithChanges(): Promise<{ success: boolean, fulfilled: string[], rejected: string[] }> {
        const documents = await this.loadDokumentInformationenFromIndexedDB();
        const documentsWithChanges = documents.filter(x => x.uploadRequired);
        const promises: Promise<ServiceAppModels.SA_Document>[] = [];
        for (const doc of documentsWithChanges) {
            promises.push(
                new Promise((resolve, reject) => {
                    this.doUploadModifiedDocumentInfos([doc]).subscribe({
                        next: () => {
                            resolve(doc);
                        }, error: (err) => {
                            reject(doc);
                        },
                    });
                })
            );
        }

        const fulfilled: string[] = [];
        const rejected: string[] = [];
        const result = await Promise.allSettled(promises);
        for (const res of result) {
            if (res.status === 'fulfilled') {
                const doc = res.value;
                doc.uploadRequired = false;
                await this.localStorageService.add(doc.documentID, doc, this.dbStorageSA_Doku);
            } else {
                console.error('Fehler beim Hochladen des Dokumentes ' + res.reason);
                rejected.push(res.reason);
            }
        }
        return {success: result.length === fulfilled.length, fulfilled, rejected};
    }

    async uploadModifiedObjects(): Promise<{ success: boolean, fulfilled: string[], rejected: string[] }> {
        const objects: ServiceAppModels.SA_Objekt[] = await this.localStorageService.get('objekte', this.dbStorageSA, false, []);
        const objectsWithChanges = objects.filter(x => x.modified);
        const promises: Promise<ServiceAppModels.SA_Objekt>[] = [];
        for (const obj of objectsWithChanges) {
            promises.push(
                new Promise((resolve, reject) => {
                    this.doUploadModifiedObjects([obj]).subscribe({
                        next: () => {
                            resolve(obj);
                        }, error: (err) => {
                            reject(obj);
                        },
                    });
                })
            );
        }

        const fulfilled: string[] = [];
        const rejected: string[] = [];
        const result = await Promise.allSettled(promises);

        for (const res of result) {
            if (res.status === 'fulfilled') {
                const i = objects.findIndex(r => r.serviceID === res.value.serviceID);
                if (i > -1) {
                    objects[i].modified = false;
                    fulfilled.push(res.value.serviceID);
                } else {
                    console.error('Fehler beim Hochladen einer Terminänderung: wasnt found again to set uploaded for ' + res.value);
                    rejected.push(res.value.serviceID);
                }
            } else {
                console.error('Fehler beim Hochladen einer Terminänderung ' + res.reason);
                rejected.push(res.reason.serviceID);
            }
        }
        await this.localStorageService.add('objekte', objects, this.dbStorageSA);
        return {success: result.length === fulfilled.length, fulfilled, rejected};
    }

    async startCleanData(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.userAuthService.getCurrentAuthenticatedUser()?.sA_DeleteDataAfter) {
                // von Datum ermitteln
                let dateFrom = new Date();
                dateFrom.setDate(dateFrom.getDate() - this.userAuthService.getCurrentAuthenticatedUser()?.sA_DeleteDataAfter!);
                dateFrom = this._datumFormatPipe.removeTimeFromDate(dateFrom);
                const dateFromTimeCompare = this._datumFormatPipe.transformOnlyDateEN(dateFrom);

                // Alte, übermittelte Zeiteinträge löschen
                if (this.dbSA_TimeControlEntries && this.dbSA_TimeControlEntries.length > 0) {
                    const countBefore = this.dbSA_TimeControlEntries.length;
                    this.dbSA_TimeControlEntries = this.dbSA_TimeControlEntries.filter((x) => !x.uploaded || (x.uploaded && x.entryDate > dateFromTimeCompare));
                    if (countBefore > this.dbSA_TimeControlEntries.length) {
                        this.saveTimeControlEntriesInIndexedDB();
                    }
                }

                // Alte, übermittelte Artikel/Aufwände löschen
                const dateFrom2 = dateFromTimeCompare + 'T00:00:00';
                if (this.dbAuftragsProdukte && this.dbAuftragsProdukte.length > 0) {
                    const countBefore = this.dbAuftragsProdukte.length;
                    this.dbAuftragsProdukte = this.dbAuftragsProdukte!.filter((x) => !x.uploaded || (x.uploaded && x.datum.toString() > dateFrom2));

                    // Nur speichern, wenn es weniger wurden
                    if (countBefore > this.dbAuftragsProdukte.length) {
                        this.localStorageService.add('artikel', this.dbAuftragsProdukte, this.dbStorageSA);
                    }
                }

                // Alte Quelldaten von Zeiteinträgen und Artikel/Aufwände löschen
                if (this.dbAuftragsZeiten && this.dbAuftragsZeiten.length > 0) {
                    if (this.userAuthService.getCurrentAuthenticatedUser()!.sA_Dontsendtimestotimecontrol === 0) {
                        // Es müssen TimeControlEntries und ServicePos berücksichtigt werden
                        const countBefore = this.dbAuftragsZeiten.length;
                        this.dbAuftragsZeiten = this.dbAuftragsZeiten.filter((x) => !x.uploadedToServicePos || !x.uploadedToTimeControlEntries || (x.uploadedToServicePos && x.uploadedToTimeControlEntries && ((x.startDate! instanceof Date && this._datumFormatPipe.removeTimeFromDate(x.startDate!) > dateFrom) // UTC - Datumsvergleich
                            || (x.startDate!.toString().length === 10 && x.startDate! > dateFromTimeCompare) // EN-Datumsvergleich
                        )));
                        if (countBefore > this.dbAuftragsZeiten.length) {
                            this.localStorageService.add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA);
                        }
                    } else {
                        // Es müssen nur die ServicePos berücksichtigt werden
                        const countBefore = this.dbAuftragsZeiten.length;
                        this.dbAuftragsZeiten = this.dbAuftragsZeiten.filter((x) => !x.uploadedToServicePos || (x.uploadedToServicePos && x.startDate! > dateFrom));
                        if (countBefore > this.dbAuftragsZeiten.length) {
                            this.localStorageService.add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA);
                        }
                    }
                }
                resolve(true);
            } else {
                resolve(true);
            }
        });
    }

    // SYNC DOWN: ENDE DOWNLOAD FROM WEBSERVICE
    public getTimesChanged$(): Observable<any> {
        return this.timesChanged$.asObservable();
    }

    async saveTimeEntryToIndexedDB(timeEntry: ServiceAppModels.SA_TimeEntry) {
        const orderTimes = await this.localStorageService.get('zeiten', this.dbStorageSA, false, []);
        orderTimes.push(timeEntry);
        await this.localStorageService.add('zeiten', orderTimes, this.dbStorageSA);
        this.timesChanged$.next(orderTimes);
    }

    async downloadDocuments(): Promise<ServiceAppSyncTask> {
        if (!this.webServiceConfig) {
            throw Error('WebServiceConfig does not exists');
        }
        return await this.serviceAppDocumentTaskFactory.createDocumentSyncTask(this.webServiceConfig);
    }

    private blobToFile(theBlob: Blob, fileName: string): File {
        const b: any = theBlob;
        // A Blob() is almost a File() - it's just missing the two properties for our use cases below which we will add
        // TODO 2025-12-09 https://swissjira.atlassian.net/browse/EN-4215 LastModifiedDate is deprecated use last modified instead
        b.lastModifiedDate = new Date();
        b.name = fileName;

        // Cast to a File() type
        return theBlob as File;
    }

    private getUniqueChecklisteFileName(objectID: string): string {
        const praefix = `Checkliste_${new Date().getTime()}`; //
        const fileExtension = '.pdf';
        let currFileName = praefix + fileExtension;
        let incValue = 0;
        while (this.dbChecklisten.find((c) => c.objectID === objectID && c.fileName === currFileName)) {
            incValue += 1;
            currFileName = praefix + '_' + incValue + fileExtension;
        }
        return currFileName;
    }

    private doUpdateAppointment(appointment: ServiceAppModels.Appointment) {
        const url = this.webServiceConfig!.saUrl;
        return this.http.post<ServiceAppModels.Appointment>(url + '/UpdateTermin', appointment, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    private async doUploadServicePos(servicePos: ServiceAppModels.SA_ServicePos[]): Promise<any> {
        const url = this.webServiceConfig!.saUrl;
        return new Promise<any>((resolve, reject) => {
            this.http.post<ServiceAppModels.SA_ServicePos[]>(url + '/UploadServicePos', servicePos, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).subscribe({
                next: (res) => {
                    resolve(res);
                }, error: (err) => {
                    reject(err);
                }
            });
        });
    }

    private doUploadTimeControlEntries(timeControlEntries: ServiceAppModels.SA_TimeControlEntry[]) {
        const url = this.webServiceConfig!.saUrl;
        return this.http.post<ServiceAppModels.SA_TimeControlEntry[]>(url + '/UploadTimeControlEntries', timeControlEntries, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    private checkStartTime(timeRecording: ServiceAppModels.TimeRecordingItem): Promise<ServiceAppModels.TimeRecordingItem> {
        return new Promise((resolve) => {
            this.localStorageService.get('zeiten', this.dbStorageSA).then((result) => {
                let times = result;

                if (!times) {
                    resolve(timeRecording);
                } else {
                    times = times.filter((value: ServiceAppModels.SA_TimeEntry) => this._datumFormatPipe.transformDateWithTime(value.endDate!) === this._datumFormatPipe.transformDateWithTime(timeRecording.startTime!));
                    console.log(times);
                    if (!times) {
                        resolve(timeRecording);
                    } else {
                        timeRecording.startTime?.setMinutes(timeRecording.startTime.getMinutes() + 1);
                    }
                    resolve(timeRecording);
                }
            });
        });


    }

    private saveTimeEntryToServicePos(timeEntry: ServiceAppModels.SA_TimeEntry) {
        try {
            let timeObj: ServiceAppModels.SA_Objekt | undefined;
            if (timeEntry.serviceID) {
                timeObj = this.sA_Objects?.filter((x) => x.serviceID === timeEntry.serviceID)[0];
            } else {
                timeObj = this.sA_Objects?.filter((x) => x.objectID === timeEntry.objectID && x.serviceID === null)[0];
            }

            if (timeObj) {
                let entryDate;
                if (timeEntry.startDate) {
                    if (timeEntry.startDate instanceof Date) {
                        entryDate = timeEntry.startDate;
                    } else {
                        try {
                            entryDate = new Date(timeEntry.startDate as string);
                        } catch (ex) {
                            // I do not expect this to happen, but there might be legacy cases (just like this already being wonky with types)
                            console.log(ex);
                            this.svcMessageBox.showInfoByLangagueKey('MAIN_APP_DIALOG.ERROR_DIALOG.SA_DATE_CONVERSION_ERROR.msg', 'MAIN_APP_DIALOG.ERROR_DIALOG.SA_DATE_CONVERSION_ERROR.title');
                        }
                    }
                }

                if (!entryDate) {
                    if (timeObj.startTime && !(timeObj.startTime instanceof Date)) {
                        entryDate = new Date(timeObj.startTime as string);
                    } else {
                        entryDate = timeObj.startTime;
                    }
                }
                // Stunden und Minuten entfernen
                if (entryDate.toString().length > 10) {
                    entryDate = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
                    const offSet = -1 * entryDate.getTimezoneOffset();
                    entryDate.setMinutes(offSet); // Korrektur der Minuten wegen der Zeitzone
                }

                let unit = 'h';
                if (this.userAuthService.getCurrentAuthenticatedUser() && this.userAuthService.getCurrentAuthenticatedUser()?.sA_HoursText) {
                    unit = this.userAuthService.getCurrentAuthenticatedUser()!.sA_HoursText!;
                }

                // Mitarbeiterzeiten ohne Spesen
                timeEntry.employeeIDS.forEach((employeeID) => {
                    // Prüfen, ob die erfasste Zeit schon in der ServicePos gespeichert wurde
                    const Index = this.dbSA_ServicePos.findIndex((x) => x.servicePosID === timeEntry.servicePosID && x.fK_EmployeeNo === employeeID && x.productType === 1);

                    let goodWill = 0;
                    if (timeEntry.goodWill && timeEntry.goodWill) {
                        goodWill = 1;
                    }

                    const stundenAnsatz = this.getDBMitarbeiterAnsatz(timeObj!.startTime!, employeeID);

                    let regieAnsatz = 0;
                    if (timeEntry.costCenterID) {
                        regieAnsatz = this.getDBKostenstellenRegieAnsatz(timeObj!.startTime!, timeEntry.costCenterID);
                    }

                    const servicePos: ServiceAppModels.SA_ServicePos = {
                        entryDate,
                        servicePosID: timeEntry.servicePosID,
                        fK_ObjectID: timeObj!.objectID,
                        fK_ServiceID: timeEntry.serviceID!,
                        fK_EmployeeNo: employeeID,
                        productType: 1,
                        fK_ProductNo: timeEntry.costCenterID.toString(),
                        fK_CostCenterID: timeEntry.costCenterID,
                        unit,
                        amount: timeEntry.duration,
                        saleprice: stundenAnsatz + regieAnsatz,
                        totalAmount: timeEntry.duration,
                        goodWill,
                        shortText: timeEntry.activityDescription,
                        mandator: timeObj!.mandator,
                        transferred: false,
                        deleted: false,
                        uploaded: false,
                    };

                    if (Index === -1) {
                        this.dbSA_ServicePos.push(servicePos);
                    } else {
                        this.dbSA_ServicePos[Index] = servicePos;
                    }
                });

                // Mitarbeiter-Spesen
                if (timeEntry.expenseID && timeEntry.expenseToCharge) {
                    const shortText = this.dbSpesen?.filter(x => x.expenseID === timeEntry.expenseID)[0].description;
                    timeEntry.employeeIDS.forEach((employeeID) => {
                        let doSave = true;
                        // Prüfen, ob die erfassten Spesen schon in der ServicePos gespeichert wurden
                        const Index = this.dbSA_ServicePos.findIndex((x) => x.expenseServicePosID === timeEntry.servicePosID && x.fK_EmployeeNo === employeeID && x.productType === 5);

                        // Prüfen, ob die Spesen nicht schon übermittelt sind
                        if (Index > -1) {
                            if (this.dbSA_ServicePos[Index].uploaded) {
                                doSave = false;
                            }
                        }

                        if (doSave) {
                            let goodWill = 0;
                            if (timeEntry.goodWill && timeEntry.goodWill) {
                                goodWill = 1;
                            }

                            let servicePosID: string;
                            if (Index === -1) {
                                servicePosID = uuidv4();
                            } else {
                                servicePosID = this.dbSA_ServicePos[Index].servicePosID;
                            }

                            const servicePos: ServiceAppModels.SA_ServicePos = {
                                entryDate,
                                servicePosID,
                                fK_ObjectID: timeObj!.objectID,
                                fK_ServiceID: timeEntry.serviceID!,
                                fK_EmployeeNo: employeeID,
                                productType: 5,
                                fK_ProductNo: '',
                                fK_CostCenterID: timeEntry.costCenterID,
                                unit: timeEntry.expenseUnit!,
                                amount: timeEntry.expenseAmount,
                                saleprice: timeEntry.expensePrice,
                                totalAmount: timeEntry.expenseAmount,
                                goodWill,
                                shortText: shortText!,
                                mandator: timeObj!.mandator,
                                transferred: false,
                                deleted: false,
                                uploaded: false,
                                expenseServicePosID: timeEntry.servicePosID
                            };

                            if (Index === -1) {
                                this.dbSA_ServicePos.push(servicePos);
                            } else {
                                this.dbSA_ServicePos[Index] = servicePos;
                            }
                        }
                    });
                }
            } else {
                this.svcMessageBox.showError('saveTimeEntryToServicePos(): Zu einer erfassten Zeit konnte kein Objekt ermittet werden. ServicePosID: ' + timeEntry.servicePosID, '!!! Fehler !!!');
            }
        } catch (ex) {
            console.error('saveTimeEntryToServicePos(): Fehler beim Speichern einer Zeit in die ServicePos.', ex);
        }
    }

    private async loadDokumentInformationenFromIndexedDB(): Promise<SA_Document[]> {
        this.dbDokumente = await this.localStorageService.get('objektDokumente', this.dbStorageSA, false, []);
        return this.dbDokumente;
    }

    private doUploadModifiedDocumentInfos(documents: ServiceAppModels.SA_Document[]): Observable<Response> {
        if (!this.webServiceConfig) {
            throw new Error();
        }
        const url = this.webServiceConfig.saUrl;
        return this.http.post<Response>(url + '/UploadDocuments', documents, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    private doUploadModifiedObjects(objects: ServiceAppModels.SA_Objekt[]): Observable<Response> {
        if (!this.webServiceConfig) {
            throw new Error();
        }
        const url = this.webServiceConfig.saUrl;
        return this.http.post<Response>(url + '/UploadObjects', objects, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    // SYNC UP: END UPLOAD TO WEBSERVICE

    // Clean

    private saveOrderProductToServicePos(productEntry: ServiceAppModels.SA_ProductEntry) {
        try {
            const timeObj = this.sA_Objects?.filter((x) => x.serviceID === productEntry.serviceID)[0];
            // Prüfen, ob die erfasste Position schon in der ServicePos gespeichert wurde
            const serPosObjIndex = this.dbSA_ServicePos.findIndex((x) => x.servicePosID === productEntry.servicePosID);

            // Wenn die Position schon übermittelt ist → nichts mehr machen (EN-1446)
            if (serPosObjIndex > -1) {
                if (this.dbSA_ServicePos[serPosObjIndex].uploaded) {
                    return;
                }
            }

            if (timeObj) {
                let entryDate = new Date(timeObj.startTime!);
                if (entryDate.toString().length > 10) {
                    entryDate = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
                    const offSet = -1 * entryDate.getTimezoneOffset();
                    entryDate.setMinutes(offSet); // Korrektur der Minuten wegen der Zeitzone
                }

                let prodType = 2; /* Artikel aus Artikelstamm */
                if (!productEntry.ausArtikelStamm) {
                    prodType = 3;
                } /* Sternartikel */
                if (productEntry.spesen) {
                    prodType = 5;
                }

                let goodWill = 0;
                if (productEntry.kulanz && productEntry.kulanz) {
                    goodWill = 1;
                }


                let mengeTotal = productEntry.mengeTotal;
                if (!mengeTotal && productEntry.anzahl) {
                    mengeTotal = productEntry.anzahl;
                }

                if (!this.userAuthService.getCurrentAuthenticatedUser()) {
                    console.error('saveOrderProductToServicePos(): Anwender nicht da!! ');
                }

                let employeeID = this.userAuthService.getCurrentAuthenticatedUser()!.employeeID;
                if (productEntry.mitarbeiter) {
                    employeeID = productEntry.mitarbeiter.employeeID;
                }

                const servicePos: ServiceAppModels.SA_ServicePos = {
                    fK_RowID: productEntry.rowID,
                    entryDate,
                    servicePosID: productEntry.servicePosID,
                    fK_ObjectID: timeObj.objectID,
                    fK_ServiceID: productEntry.serviceID,
                    fK_EmployeeNo: employeeID,
                    productType: prodType,
                    fK_ProductNo: productEntry.artikelnummer,
                    unit: productEntry.einheit,
                    shortText: productEntry.bezeichnung,
                    mandator: timeObj.mandator,
                    transferred: false,
                    amount: productEntry.anzahl,
                    saleprice: productEntry.preisProEinheit,
                    goodWill,
                    length: productEntry.laenge,
                    width: productEntry.breite,
                    thickness: productEntry.dicke, // weightPerUnit:    Braucht es das???
                    totalAmount: mengeTotal,
                    deleted: false,
                    uploaded: false,
                };

                if (serPosObjIndex === -1) {
                    this.dbSA_ServicePos.push(servicePos);
                } else {
                    this.dbSA_ServicePos[serPosObjIndex] = servicePos;
                }
            } else {
                this.svcMessageBox.showError('saveOrderProductToServicePos(): Zu einem erfassten Produkt konnte kein Objekt ermittet werden. ServicePosID: ' + productEntry.servicePosID, '!!! Fehler !!!');
            }
        } catch (ex) {
            console.error('saveOrderProductToServicePos(): Fehler beim Speichern eines Produktes in die ServicePos.', ex);
        }
    }

    // da die Verlinkung eine Zirkulation auslöst.
    private uploadDokument(dokuInfo: DokumentModels.DokumentInfo, file: File): Observable<Response> {
        if (!this.webServiceConfig) {
            throw new Error();
        }

        const formData = new FormData();
        formData.append('fileName', dokuInfo.fileName);
        formData.append('description', dokuInfo.fileName.split('.')[0]);
        formData.append('documentType', '9');
        formData.append('objectID', dokuInfo.objectID);
        formData.append('documentID', dokuInfo.documentID);
        // formData.append("concurrencyCheck", dokuInfo.timestamp.toISOString());  //TODO get timestamp from somewhere, make sure to include timezone
        formData.append('file', file);

        const url = this.webServiceConfig.dokuUrl;
        return this.http.post<Response>(url + '/UploadDoku', formData);
    }

    private uploadTimeJournalToTimeControlEntries() {
        return new Promise((resolve) => {
            // Zeiten hochladen (TimeJournal)
            if (this.dbAuftragsZeiten) {
                if (this.dbAuftragsZeiten.filter((x) => !x.uploadedToTimeControlEntries).length > 0) {
                    let doUpdateIndexDB = false;
                    // Erfasste Zeiten in die TimeControlEntries schreiben
                    this.dbSA_TimeControlEntries = [];
                    this.dbAuftragsZeiten.filter((x) => !x.uploadedToTimeControlEntries).forEach((sa_TimeEntry) => {
                        this.saveTimeEntryToTimeControlEntries(sa_TimeEntry);
                        doUpdateIndexDB = true;
                    });
                    if (doUpdateIndexDB) {
                        this.saveTimeControlEntriesInIndexedDB();
                    }

                    if (this.dbSA_TimeControlEntries.filter((x) => !x.uploaded).length > 0) {
                        const posToUpload: ServiceAppModels.SA_TimeControlEntry[] = [];
                        // Es dürfen nur Positionen übermittelt werden, wo der Tag abgeschlossen ist
                        // und die Position nicht auf gelöscht gesetzt ist
                        this.dbSA_TimeControlEntries.filter((x) => !x.uploaded && !x.deleted).forEach((timeControlEntry) => {
                            if (this.isDayClosed(timeControlEntry.entryDate)) {
                                posToUpload.push(timeControlEntry);
                            }
                        });

                        if (posToUpload.length > 0) {
                            this.doUploadTimeControlEntries(posToUpload).subscribe({
                                next: (res) => {
                                    const resultList: ServiceAppModels.SA_TimeControlEntryResult[] = (res as any).timeControlEntryResultList;
                                    if (this.svcSwissSettings.getWebConfig().debugMode) {
                                        console.log('resultList timeControlEntries', resultList);
                                    }
                                    resultList.forEach((timeControlEntryRes) => {
                                        // Die Position konnte erfolgreich gespeichert werden
                                        if (!timeControlEntryRes.hasError) {
                                            const index = this.dbAuftragsZeiten!.findIndex((x) => x.servicePosID === timeControlEntryRes.servicePosID);
                                            // Prüfen, ob die Zeitzeile eventuell mehrere Mitarbeiter hatte
                                            // Falls ja => dann müssen alle Mitarbeiter geprüft werden.
                                            const allePos = resultList.filter((x) => x.servicePosID === timeControlEntryRes.servicePosID).length;
                                            const allePosOhneFehler = resultList.filter((x) => x.servicePosID === timeControlEntryRes.servicePosID && !x.hasError).length;
                                            if (allePos === allePosOhneFehler) {

                                                // Keine Fehler vorhanden!
                                                if (index >= 0) {
                                                    const auftragsZeit = this.dbAuftragsZeiten![index];
                                                    if (auftragsZeit) {
                                                        this.dbAuftragsZeiten![index].uploadedToTimeControlEntries = true;
                                                        this.dbSA_TimeControlEntries.filter((x) => x.servicePosID === timeControlEntryRes.servicePosID && x.rowID === timeControlEntryRes.rowID)[0].uploaded = true;
                                                    } else {
                                                        console.error('uploadTimeJournalToTimeControlEntries(): auftragszeit existiert nicht. ', timeControlEntryRes);
                                                    }
                                                } else {
                                                    console.error('uploadTimeJournalToTimeControlEntries(): index nicht gefunden. TimeControlEntryRes: ', timeControlEntryRes);
                                                }
                                            } else {
                                                // Hier muss eine Liste mit Fehlern erstellt werden.
                                                this.showSyncMessage('!!! Fehler !!! Alle => (saveTimeControlEntriesInIndexedDB() 1): Position konnte nicht gespeichert werden. RowID: ' + timeControlEntryRes.rowID);
                                            }
                                        } else {
                                            // Hier muss eine Liste mit Fehlern erstellt werden.
                                            this.showSyncMessage('!!! Fehler !!! (saveTimeControlEntriesInIndexedDB() 2): Position konnte nicht gespeichert werden. RowID: ' + timeControlEntryRes.rowID);
                                        }
                                    });
                                    // Angepasste Zeiten in IndexedDB speichern
                                    this.localStorageService.add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA);
                                    this.localStorageService.add('timeControlEntries', this.dbSA_TimeControlEntries, this.dbStorageSA);
                                    resolve(true);
                                }, error: (error) => {
                                    console.error('Fehler beim Übermitteln der Service-Positionen: ' + error);
                                    resolve(false);
                                },
                            });
                        } else {
                            resolve(true);
                        }
                    } else {
                        // keine Daten für Upload vorhanden
                        resolve(true);
                    }
                } else {
                    // Keine Daten für den Upload vorhanden
                    resolve(true);
                }
            }
        });
    }

    private uploadTimeJournalToServicePos(): Promise<boolean> {
        return new Promise(async (resolve) => {
            let errorExists = false;

            // Erfasste Zeiten in die Service-Pos schreiben
            if (this.dbAuftragsZeiten) {
                if (this.dbAuftragsZeiten.filter((x) => !x.uploadedToServicePos).length > 0) {
                    let doUpdateIndexDB = false;
                    this.dbAuftragsZeiten.forEach((saTimeEntry) => {
                        if (!saTimeEntry.uploadedToServicePos && saTimeEntry.serviceID !== null) {
                            // Zeiten werden nur für die Weiterverarbeitung gespeichert, wenn der Service abgeschlossen ist
                            const termin = this.dbTermine?.filter((x) => x.serviceID === saTimeEntry.serviceID)[0];
                            if (termin && termin.serviceComplete === 1) {
                                this.saveTimeEntryToServicePos(saTimeEntry);
                                doUpdateIndexDB = true;
                            }
                        }
                    });
                    if (doUpdateIndexDB) {
                        await this.saveServicePosInIndexedDB();
                    }

                    if (this.dbSA_ServicePos) {
                        const servicePos = this.dbSA_ServicePos.filter((x) => !x.uploaded);
                        if (this.svcSwissSettings.getWebConfig().debugMode) {
                            console.log('servicePos to Upload from dbAuftragsZeiten', servicePos);
                        }
                        if (servicePos && servicePos.length > 0) {
                            this.doUploadServicePos(servicePos).then(res => {
                                const resultList: ServiceAppModels.SA_ServicePosResult[] = (res as any).servicePosResultList;
                                if (this.svcSwissSettings.getWebConfig().debugMode) {
                                    console.log('resultList ServicePos', resultList);
                                }
                                resultList.forEach((servicePosRes) => {
                                    // Die Position konnte erfolgreich gespeichert werden
                                    if (!servicePosRes.hasError) {
                                        const index = this.dbAuftragsZeiten!.findIndex((x) => x.servicePosID === servicePosRes.servicePosID);
                                        // Prüfen, ob die Zeitzeile eventuell mehrere Mitarbeiter hatte
                                        // Falls ja => dann müssen alle Mitarbeiter geprüft werden.
                                        const allePos = resultList.filter((x) => x.servicePosID === servicePosRes.servicePosID).length;
                                        const allePosOhneFehler = resultList.filter((x) => x.servicePosID === servicePosRes.servicePosID && !x.hasError).length;
                                        if (allePos === allePosOhneFehler) {
                                            // Keine Fehler vorhanden!
                                            if (index >= 0) {
                                                this.dbAuftragsZeiten![index].uploadedToServicePos = true;
                                                this.dbSA_ServicePos.filter((x) => x.servicePosID === servicePosRes.servicePosID && x.fK_EmployeeNo === servicePosRes.fK_EmployeeNo)[0].uploaded = true;
                                            }
                                        } else {
                                            // Hier muss eine Liste mit Fehlern erstellt werden.
                                            this.showSyncMessage('!!! Fehler !!! Alle => (uploadTimeJournalToServicePos() 1): Position konnte nicht gespeichert werden. ServicePosID: ' + servicePosRes.servicePosID);
                                            errorExists = true;
                                        }
                                    } else {
                                        // Hier muss eine Liste mit Fehlern erstellt werden.
                                        this.showSyncMessage('!!! Fehler !!! (uploadTimeJournalToServicePos() 2): Position konnte nicht gespeichert werden. ServicePosID: ' + servicePosRes.servicePosID);
                                        errorExists = true;
                                    }
                                });
                                // Angepasste Zeiten in IndexedDB speichern
                                this.localStorageService.add('zeiten', this.dbAuftragsZeiten, this.dbStorageSA);
                                this.localStorageService.add('servicePos', this.dbSA_ServicePos, this.dbStorageSA);
                                if (errorExists) {
                                    resolve(false);
                                } else {
                                    resolve(true);
                                }
                            }).catch((error) => {
                                console.error('Fehler beim Übermitteln der Service-Positionen: ' + error);
                                resolve(false);
                            });
                        } else {
                            // Keine Daten für den Upload vorhanden
                            resolve(true);
                        }
                    } else {
                        // Keine Daten für den Upload vorhanden
                        resolve(true);
                    }
                } else {
                    // Keine Daten für den Upload vorhanden
                    resolve(true);
                }
            }
        });
    }

    private refreshServicePositions(): Observable<boolean> {
        if (!this.webServiceConfig) {
            throw new Error();
        }
        const url = this.webServiceConfig.saUrl;
        return this.http.get<boolean>(url + '/RefreshServicePositionen?mandant=' + this.userAuthService.getCurrentAuthenticatedUser()?.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }
}
